<template>
    <div class="w-100">
        <!-- Page title -->
        <div class="row align-items-center mt-1 mb-3">
            <div class="col-9">
                <h2 class="page-title">
                    {{ pageTitle }}
                </h2>
            </div>
            <!-- Progress bar -->
            <div v-if="progressBar == true" class="col-3 ms-auto d-print-none d-flex align-items-center" id="progressBar">
                <span class="small me-3 w-50 text-end">{{ percentageComplete }}% Complete</span>
                <b-progress class="w-50" :value="currentStep" :max="maxStep" animated></b-progress>
            </div>
        </div>

        <!-- Alert box -->
        <div v-if="this.v$.step[this.currentStep - 1].$error" class="alert alert-danger alert-dismissible" role="alert">
            <div class="d-flex">
                <div>
                    <!-- Download SVG icon from http://tabler-icons.io/i/alert-circle -->
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="icon alert-icon"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        stroke-width="2"
                        stroke="currentColor"
                        fill="none"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    >
                        <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                        <path d="M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0"></path>
                        <path d="M12 8v4"></path>
                        <path d="M12 16h.01"></path>
                    </svg>
                </div>
                <div>
                    <span v-if="v$.$errors.length > 1"> Sorry, there are ({{ this.v$.step[this.currentStep - 1].$errors.length }}) errors found please correct the errors to proceed.</span>
                    <span v-if="v$.$errors.length <= 1"> Sorry, there's ({{ this.v$.step[this.currentStep - 1].$errors.length }}) error found please correct the error to proceed.</span>
                </div>
            </div>
            <a class="btn-close" data-bs-dismiss="alert" aria-label="close"></a>
        </div>

        <form id="create-nursing-assessment" action="nursing-assessments/store" class="add-nursing-assessment add-record">
            <div class="card" v-if="currentStep == 1" title="Activity Level">
                <div class="card-header">
                    <h3 class="card-title">Activity Level</h3>
                </div>
                <div class="card-body">
                    <div class="row gx-5">
                        <div class="col-sm-10 col-md-8 col-xl-6">
                            <div class="mb-3">
                                <div class="form-label">Time of day for optional activity</div>
                                <input
                                    type="time"
                                    class="form-control w-50"
                                    name="optional-activity-time"
                                    placeholder="16:00"
                                    v-model="step[0].optionalActivityTime"
                                    :class="{'is-invalid': v$.step[0].optionalActivityTime.$invalid}"
                                />
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[0].optionalActivityTime.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>
                            <div class="mb-3">
                                <div class="form-label">Preferred Activities</div>

                                <multiselect
                                    name="preferredActivities"
                                    v-model="step[0].preferredActivities"
                                    :class="{'is-invalid': v$.step[0].preferredActivities.$invalid}"
                                    :options="this.preferredActivitiesOptions"
                                    :searchable="true"
                                    :close-on-select="true"
                                    :show-labels="true"
                                    :taggable="true"
                                    :multiple="true"
                                    title=""
                                    deselect-label="Deselect"
                                    select-label="Select"
                                    tag-placeholder="Add this as new tag"
                                    placeholder="Select or add a new activity"
                                    @tag="addTagPreferredActivities"
                                ></multiselect>

                                <div class="invalid-feedback" :class="{'d-block': v$.step[0].preferredActivities.$invalid}">Invalid feedback</div>
                            </div>
                            <div class="mb-3">
                                <div class="form-label">Interests / Hobbies</div>
                                <multiselect
                                    name="interestsHobbies"
                                    v-model="step[0].interestsHobbies"
                                    :class="{'is-invalid': v$.step[0].interestsHobbies.$invalid}"
                                    :options="this.interestsHobbiesOptions"
                                    :searchable="true"
                                    :close-on-select="true"
                                    :show-labels="true"
                                    :taggable="true"
                                    :multiple="true"
                                    title=""
                                    deselect-label="Deselect"
                                    select-label="Select"
                                    tag-placeholder="Add this as new tag"
                                    placeholder="Select or add a new hobby"
                                    @tag="addTagInterestsHobbies"
                                ></multiselect>

                                <div class="invalid-feedback" :class="{'d-block': v$.step[0].interestsHobbies.$invalid}">Invalid feedback</div>
                            </div>
                        </div>

                        <div class="col-sm-10 col-md-8 col-xl-6">
                            <div class="mb-4 row">
                                <div class="col-6">
                                    <div class="form-label">Oriented to?</div>
                                    <div class="form-check form-switch mb-0 ms-1">
                                        <input class="form-check-input" type="checkbox" name="oriented-to-time" v-model="step[0].orientedToTime" />
                                        <span class="form-check-label">Time</span>
                                    </div>
                                    <div class="form-check form-switch mb-0 ms-1 mt-2">
                                        <input class="form-check-input" type="checkbox" name="oriented-to-place" v-model="step[0].orientedToPlace" />
                                        <span class="form-check-label">Place</span>
                                    </div>
                                    <div class="invalid-feedback" :class="{'d-block': v$.step[0].orientedToTime.$invalid}">Invalid feedback</div>
                                    <div class="invalid-feedback" :class="{'d-block': v$.step[0].orientedToPlace.$invalid}">Invalid feedback</div>
                                </div>
                                <div class="col-6">
                                    <div class="form-label">Recognises?</div>
                                    <div class="form-check form-switch mb-0 ms-1">
                                        <input class="form-check-input" type="checkbox" name="recognises-family" v-model="step[0].recognisesFamily" />
                                        <span class="form-check-label">Family</span>
                                    </div>
                                    <div class="form-check form-switch mb-0 ms-1 mt-2">
                                        <input class="form-check-input" type="checkbox" name="recognises-staff" v-model="step[0].recognisesStaff" />
                                        <span class="form-check-label">Staff</span>
                                    </div>
                                    <div class="invalid-feedback" :class="{'d-block': v$.step[0].recognisesFamily.$invalid}">Invalid feedback</div>
                                    <div class="invalid-feedback" :class="{'d-block': v$.step[0].recognisesStaff.$invalid}">Invalid feedback</div>
                                </div>
                            </div>
                            <div class="mb-3">
                                <div class="form-label">Understands/Participates in conversations</div>
                                <label v-for="option in yesNoDontKnowOptions" class="form-check form-check-inline">
                                    <input class="form-check-input" type="radio" name="understandsConversation" :value="option" v-model="step[0].understandsConversation" />
                                    <span class="form-check-label">{{ option }}</span>
                                </label>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[0].understandsConversation.$invalid}">Invalid feedback</div>
                            </div>
                            <div class="mb-3">
                                <div class="form-label">Attention Span</div>
                                <label v-for="option in goodPoorFairOptions" class="form-check form-check-inline">
                                    <input class="form-check-input" type="radio" name="attention-span" :value="option" v-model="step[0].attentionSpan" />
                                    <span class="form-check-label">{{ option }}</span>
                                </label>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[0].attentionSpan.$invalid}">Invalid feedback</div>
                            </div>
                            <div class="mb-3">
                                <div class="form-label">Other Comments</div>
                                <textarea
                                    rows="4"
                                    class="form-control"
                                    placeholder="Comments"
                                    value=""
                                    spellcheck="false"
                                    name="other-comments"
                                    v-model="step[0].otherCommentsActivity"
                                    :class="{
                                        'is-invalid': v$.step[0].otherCommentsActivity.$invalid,
                                    }"
                                ></textarea>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[0].otherCommentsActivity.$invalid}">Invalid feedback</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="card-footer">
                    <p class="m-0 text-muted">
                        <a href="#" class="save-btn invisible">Save and continue later</a>
                    </p>
                    <p class="d-flex align-items-center mb-0">
                        <a @click="onClickNext" class="ml-5 btn btn-primary">Next</a>
                    </p>
                </div>
            </div>

            <div class="card" v-if="currentStep == 2" title="Sleep Patterns">
                <div class="card-header">
                    <h3 class="card-title">Sleep Patterns</h3>
                </div>
                <div class="card-body">
                    <div class="row gx-5">
                        <div class="col-sm-10 col-md-8 col-xl-6">
                            <div class="mb-3 row">
                                <div class="col-5">
                                    <div class="form-label">Preferred bedtime</div>
                                    <input
                                        type="time"
                                        class="form-control w-100"
                                        name="preferred-bedtime"
                                        placeholder="16:00"
                                        v-model="step[1].preferredBedtime"
                                        :class="{'is-invalid': v$.step[1].preferredBedtime.$invalid}"
                                    />
                                    <div
                                        class="invalid-feedback"
                                        :class="{
                                            'd-block': v$.step[1].preferredBedtime.$invalid,
                                        }"
                                    >
                                        Invalid feedback
                                    </div>
                                </div>
                                <div class="col-5">
                                    <div class="form-label">Preferred waking time</div>
                                    <input
                                        type="time"
                                        class="form-control w-100"
                                        name="preferred-walking-time"
                                        placeholder="16:00"
                                        v-model="step[1].preferredWakingTime"
                                        :class="{'is-invalid': v$.step[1].preferredWakingTime.$invalid}"
                                    />
                                    <div
                                        class="invalid-feedback"
                                        :class="{
                                            'd-block': v$.step[1].preferredWakingTime.$invalid,
                                        }"
                                    >
                                        Invalid feedback
                                    </div>
                                </div>
                            </div>
                            <div class="mb-3 row">
                                <div class="col-5">
                                    <div class="form-label">Number of Pillows</div>
                                    <div class="d-flex align-items-center">
                                        <select
                                            class="form-select w-100"
                                            name="number-of-pillows"
                                            v-model="step[1].numberOfPillows"
                                            :class="{
                                                'is-invalid': v$.step[1].numberOfPillows.$invalid,
                                            }"
                                        >
                                            <option v-for="(n, i) in pillowsMaxCount + 1" :value="i">
                                                {{ i }}
                                            </option>
                                        </select>
                                    </div>
                                    <div class="invalid-feedback" :class="{'d-block': v$.step[1].numberOfPillows.$invalid}">Invalid feedback</div>
                                </div>
                                <div class="col-5"></div>
                            </div>
                            <div class="mb-3 row">
                                <div class="col-5">
                                    <div class="form-label">Wakes up at night</div>
                                    <input
                                        type="time"
                                        class="form-control w-100"
                                        name="wakes-up-at-night"
                                        placeholder="16:00"
                                        v-model="step[1].wakesUpAtNightTime"
                                        :class="{'is-invalid': v$.step[1].wakesUpAtNightTime.$invalid}"
                                    />
                                    <div
                                        class="invalid-feedback"
                                        :class="{
                                            'd-block': v$.step[1].wakesUpAtNightTime.$invalid,
                                        }"
                                    >
                                        Invalid feedback
                                    </div>
                                </div>
                                <div class="col-5">
                                    <div class="form-label">Due to</div>
                                    <input
                                        type="text"
                                        class="form-control w-100"
                                        name="wakes-up-at-night-reason"
                                        placeholder=""
                                        v-model="step[1].wakesUpAtNightReason"
                                        :class="{'is-invalid': v$.step[1].wakesUpAtNightReason.$invalid}"
                                    />
                                    <div
                                        class="invalid-feedback"
                                        :class="{
                                            'd-block': v$.step[1].wakesUpAtNightReason.$invalid,
                                        }"
                                    >
                                        Invalid feedback
                                    </div>
                                </div>
                            </div>
                            <div class="mb-3">
                                <div class="form-label">Dreams/Nightmares/Fears</div>
                                <label v-for="option in yesNoOptions" class="form-check form-check-inline">
                                    <input class="form-check-input" type="radio" :value="option" v-model="step[1].dreamsNightmaresFears" name="dreamsNightmaresFears" />
                                    <span class="form-check-label">{{ option }}</span>
                                </label>
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[1].dreamsNightmaresFears.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>
                            <div class="mb-3">
                                <div class="form-label">Wanders at night</div>
                                <label v-for="option in yesNoOptions" class="form-check form-check-inline">
                                    <input class="form-check-input" type="radio" :value="option" v-model="step[1].wandersAtNight" name="wandersAtNight" />
                                    <span class="form-check-label">{{ option }}</span>
                                </label>
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[1].wandersAtNight.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>
                        </div>

                        <div class="col-sm-10 col-md-8 col-xl-6">
                            <div class="mb-3">
                                <div class="form-label">Sleep aids</div>
                                <div class="form-check form-switch mb-0 ms-1">
                                    <input class="form-check-input" type="checkbox" name="sleep-aid-warm-drink" v-model="step[1].sleepAidWarmDrink" />
                                    <span class="form-check-label">Warm Drink</span>
                                </div>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[1].sleepAidWarmDrink.$invalid}">Invalid feedback</div>

                                <div class="form-check form-switch mb-0 ms-1 mt-2">
                                    <input class="form-check-input" type="checkbox" name="sleep-aid-medication" v-model="step[1].sleepAidMedication" />
                                    <span class="form-check-label">Medication</span>
                                </div>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[1].sleepAidMedication.$invalid}">Invalid feedback</div>

                                <div class="form-check form-switch mb-0 ms-1 mt-2">
                                    <input class="form-check-input" type="checkbox" name="sleep-aid-radio-music" v-model="step[1].sleepAidRadioMusic" />
                                    <span class="form-check-label">Radio/Music</span>
                                </div>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[1].sleepAidRadioMusic.$invalid}">Invalid feedback</div>
                            </div>
                            <div class="mb-3">
                                <div class="form-label">Daytime rests/naps</div>
                                <label v-for="option in yesNoOptions" class="form-check form-check-inline">
                                    <input class="form-check-input" type="radio" :value="option" v-model="step[1].daytimeRestsNaps" name="daytimeRestsNaps" />
                                    <span class="form-check-label">{{ option }}</span>
                                </label>
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[1].daytimeRestsNaps.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>
                            <div class="mb-3">
                                <div class="form-label">Other Comments</div>
                                <textarea
                                    rows="4"
                                    class="form-control w-75"
                                    placeholder="Comments"
                                    value=""
                                    spellcheck="false"
                                    name="other-comments"
                                    v-model="step[1].otherCommentsSleepPatterns"
                                    :class="{
                                        'is-invalid': v$.step[1].otherCommentsSleepPatterns.$invalid,
                                    }"
                                ></textarea>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[1].otherCommentsSleepPatterns.$invalid}">Invalid feedback</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="card-footer">
                    <p class="m-0 text-muted">
                        <a href="#" class="save-btn">Save and continue later</a>
                    </p>
                    <p class="d-flex align-items-center mb-0">
                        <a @click="onClickBack" href="#" class="p-2 mx-3 d-block back-btn">Back</a>
                        <a @click="onClickNext" class="ml-5 btn btn-primary">Next</a>
                    </p>
                </div>
            </div>

            <div class="card" v-if="currentStep == 3" title="Mobility">
                <div class="card-header">
                    <h3 class="card-title">Mobility</h3>
                </div>
                <div class="card-body">
                    <div class="row gx-5">
                        <div class="col-sm-10 col-md-8 col-xl-6">
                            <div class="mb-3">
                                <div class="form-label">Fully Mobile</div>
                                <label v-for="option in yesNoOptions" class="form-check form-check-inline">
                                    <input class="form-check-input" type="radio" :value="option" v-model="step[2].fullyMobile" name="fully-mobile" />
                                    <span class="form-check-label">{{ option }}</span>
                                </label>
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[2].fullyMobile.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Condition of feet and nails</div>
                                <input
                                    type="text"
                                    class="form-control w-75"
                                    name="feet-and-nail-condition"
                                    placeholder=""
                                    v-model="step[2].feetAndNailCondition"
                                    :class="{'is-invalid': v$.step[2].feetAndNailCondition.$invalid}"
                                />
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[2].feetAndNailCondition.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Describe any limitations in movement</div>
                                <textarea
                                    rows="3"
                                    class="form-control w-75"
                                    placeholder="Description"
                                    value=""
                                    spellcheck="false"
                                    name="limitations-in-movement-description"
                                    v-model="step[2].limitationsInMovementDescription"
                                    :class="{
                                        'is-invalid': v$.step[2].limitationsInMovementDescription.$invalid,
                                    }"
                                ></textarea>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[2].limitationsInMovementDescription.$invalid}">Invalid feedback</div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Podiatry required</div>
                                <label v-for="option in yesNoOptions" class="form-check form-check-inline">
                                    <input class="form-check-input" type="radio" :value="option" v-model="step[2].podiatryRequired" name="podiatry-required" />
                                    <span class="form-check-label">{{ option }}</span>
                                </label>
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[2].podiatryRequired.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Mobility aids used</div>
                                <input
                                    type="text"
                                    class="form-control w-75"
                                    name="mobility-aids-used"
                                    placeholder=""
                                    v-model="step[2].mobilityAidsUsed"
                                    :class="{'is-invalid': v$.step[2].mobilityAidsUsed.$invalid}"
                                />
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[2].mobilityAidsUsed.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>
                            <div class="mb-3">
                                <div class="form-label">Wandering</div>
                                <label v-for="option in yesNoOptions" class="form-check form-check-inline">
                                    <input class="form-check-input" type="radio" :value="option" v-model="step[2].wandering" name="wandering" />
                                    <span class="form-check-label">{{ option }}</span>
                                </label>
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[2].wandering.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>
                        </div>

                        <div class="col-sm-10 col-md-8 col-xl-6">
                            <div class="mb-3">
                                <div class="form-label">Describe type of wandering and level of safety</div>
                                <textarea
                                    rows="3"
                                    class="form-control w-75"
                                    placeholder="Description"
                                    value=""
                                    spellcheck="false"
                                    name="typeOfWanderingDescription"
                                    v-model="step[2].typeOfWanderingDescription"
                                    :class="{
                                        'is-invalid': v$.step[2].typeOfWanderingDescription.$invalid,
                                    }"
                                ></textarea>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[2].typeOfWanderingDescription.$invalid}">Invalid feedback</div>
                            </div>
                            <div class="mb-3">
                                <div class="form-label">Type of footwear preferred</div>
                                <input
                                    type="text"
                                    class="form-control w-75"
                                    name="typeOfPreferredFootwear"
                                    placeholder=""
                                    v-model="step[2].typeOfPreferredFootwear"
                                    :class="{'is-invalid': v$.step[2].typeOfPreferredFootwear.$invalid}"
                                />
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[2].typeOfPreferredFootwear.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>

                                <div class="form-check form-switch mb-0 ms-1 mt-2">
                                    <input class="form-check-input" type="checkbox" name="oriented-to-time" v-model="step[2].typeOfPreferredFootwearSafe" />
                                    <span class="form-check-label">this is safe</span>
                                </div>

                                <div class="invalid-feedback" :class="{'d-block': v$.step[2].typeOfPreferredFootwearSafe.$invalid}">Invalid feedback</div>
                            </div>

                            <div class="mb-3 row">
                                <div class="col-12">
                                    <div class="form-label">Risk of falling</div>
                                </div>
                                <div class="col-12 col-md-5 mt-1">
                                    <div class="form-check form-switch mb-0 ms-1 mt-2">
                                        <input class="form-check-input" type="checkbox" name="riskOfFallingBalance" v-model="step[2].riskOfFallingBalance" />
                                        <span class="form-check-label">Poor Balance</span>
                                    </div>

                                    <div class="invalid-feedback" :class="{'d-block': v$.step[2].riskOfFallingBalance.$invalid}">Invalid feedback</div>
                                </div>
                                <div class="col-12 col-md-7 mt-2 mt-md-1">
                                    <input
                                        type="text"
                                        class="form-control w-75"
                                        name="riskOfFallingBalanceComment"
                                        placeholder="Comments"
                                        v-model="step[2].riskOfFallingBalanceComment"
                                        :class="{'is-invalid': v$.step[2].riskOfFallingBalanceComment.$invalid}"
                                    />

                                    <div class="invalid-feedback" :class="{'d-block': v$.step[2].riskOfFallingBalanceComment.$invalid}">Invalid feedback</div>
                                </div>

                                <div class="col-12 col-md-5 mt-1">
                                    <div class="form-check form-switch mb-0 ms-1 mt-2">
                                        <input class="form-check-input" type="checkbox" name="riskOfFallingLimbStrength" v-model="step[2].riskOfFallingLimbStrength" />
                                        <span class="form-check-label">Poor limb strength</span>
                                    </div>

                                    <div class="invalid-feedback" :class="{'d-block': v$.step[2].riskOfFallingLimbStrength.$invalid}">Invalid feedback</div>
                                </div>
                                <div class="col-12 col-md-7 mt-2 mt-md-1">
                                    <input
                                        type="text"
                                        class="form-control w-75"
                                        name="riskOfFallingLimbStrengthComment"
                                        placeholder="Comments"
                                        v-model="step[2].riskOfFallingLimbStrengthComment"
                                        :class="{'is-invalid': v$.step[2].riskOfFallingLimbStrengthComment.$invalid}"
                                    />

                                    <div class="invalid-feedback" :class="{'d-block': v$.step[2].riskOfFallingLimbStrengthComment.$invalid}">Invalid feedback</div>
                                </div>

                                <div class="col-12 col-md-5 mt-1">
                                    <div class="form-check form-switch mb-0 ms-1 mt-2">
                                        <input class="form-check-input" type="checkbox" name="riskOfFallingMobility" v-model="step[2].riskOfFallingMobility" />
                                        <span class="form-check-label">Poor mobility</span>
                                    </div>

                                    <div class="invalid-feedback" :class="{'d-block': v$.step[2].riskOfFallingMobility.$invalid}">Invalid feedback</div>
                                </div>
                                <div class="col-12 col-md-7 mt-2 mt-md-1">
                                    <input
                                        type="text"
                                        class="form-control w-75"
                                        name="riskOfFallingMobilityComment"
                                        placeholder="Comments"
                                        v-model="step[2].riskOfFallingMobilityComment"
                                        :class="{'is-invalid': v$.step[2].riskOfFallingMobilityComment.$invalid}"
                                    />

                                    <div class="invalid-feedback" :class="{'d-block': v$.step[2].riskOfFallingMobilityComment.$invalid}">Invalid feedback</div>
                                </div>

                                <div class="col-12 col-md-5 mt-1">
                                    <div class="form-check form-switch mb-0 ms-1 mt-2">
                                        <input class="form-check-input" type="checkbox" name="riskOfFallingUseOfAids" v-model="step[2].riskOfFallingUseOfAids" />
                                        <span class="form-check-label">Poor use of aids</span>
                                    </div>

                                    <div class="invalid-feedback" :class="{'d-block': v$.step[2].riskOfFallingUseOfAids.$invalid}">Invalid feedback</div>
                                </div>
                                <div class="col-12 col-md-7 mt-2 mt-md-1">
                                    <input
                                        type="text"
                                        class="form-control w-75"
                                        name="riskOfFallingUseOfAidsComment"
                                        placeholder="Comments"
                                        v-model="step[2].riskOfFallingUseOfAidsComment"
                                        :class="{'is-invalid': v$.step[2].riskOfFallingUseOfAidsComment.$invalid}"
                                    />

                                    <div class="invalid-feedback" :class="{'d-block': v$.step[2].riskOfFallingUseOfAidsComment.$invalid}">Invalid feedback</div>
                                </div>

                                <div class="col-12 col-md-5 mt-1">
                                    <div class="form-check form-switch mb-0 ms-1 mt-2">
                                        <input class="form-check-input" type="checkbox" name="riskOfFallingSight" v-model="step[2].riskOfFallingSight" />
                                        <span class="form-check-label">Poor sight</span>
                                    </div>

                                    <div class="invalid-feedback" :class="{'d-block': v$.step[2].riskOfFallingSight.$invalid}">Invalid feedback</div>
                                </div>
                                <div class="col-12 col-md-7 mt-2 mt-md-1">
                                    <input
                                        type="text"
                                        class="form-control w-75"
                                        name="riskOfFallingSightComment"
                                        placeholder="Comments"
                                        v-model="step[2].riskOfFallingSightComment"
                                        :class="{'is-invalid': v$.step[2].riskOfFallingSightComment.$invalid}"
                                    />

                                    <div class="invalid-feedback" :class="{'d-block': v$.step[2].riskOfFallingSightComment.$invalid}">Invalid feedback</div>
                                </div>

                                <div class="col-12 col-md-5 mt-1">
                                    <div class="form-check form-switch mb-0 ms-1 mt-2">
                                        <input class="form-check-input" type="checkbox" name="riskOfFallingMentalState" v-model="step[2].riskOfFallingMentalState" />
                                        <span class="form-check-label">Poor mental state</span>
                                    </div>

                                    <div class="invalid-feedback" :class="{'d-block': v$.step[2].riskOfFallingMentalState.$invalid}">Invalid feedback</div>
                                </div>
                                <div class="col-12 col-md-7 mt-2 mt-md-1">
                                    <input
                                        type="text"
                                        class="form-control w-75"
                                        name="riskOfFallingMentalStateComment"
                                        placeholder="Comments"
                                        v-model="step[2].riskOfFallingMentalStateComment"
                                        :class="{'is-invalid': v$.step[2].riskOfFallingMentalStateComment.$invalid}"
                                    />

                                    <div class="invalid-feedback" :class="{'d-block': v$.step[2].riskOfFallingMentalStateComment.$invalid}">Invalid feedback</div>
                                </div>
                            </div>

                            <!--                             
                            <div class="mb-3 row">
                                <div class="col-5">
                                    <div class="form-label">Level of Risk</div>
                                    <div class="d-flex align-items-center">
                                        <select
                                            class="form-select w-100"
                                            name="levelOfRisk"
                                            v-model="step[2].levelOfRisk"
                                            :class="{
                                                'is-invalid': v$.step[2].levelOfRisk.$invalid,
                                            }"
                                        >
                                            <option v-for="(n, i) in levelOfRiskMaxCount + 1" :value="i">
                                                {{ i }}
                                            </option>
                                        </select>
                                    </div>
                                    <div class="invalid-feedback" :class="{'d-block': v$.step[2].levelOfRisk.$invalid}">Invalid feedback</div>
                                </div>
                                <div class="col-5">
                                </div>
                            </div>                             -->
                        </div>
                    </div>
                </div>
                <div class="card-footer">
                    <p class="m-0 text-muted">
                        <a href="#" class="save-btn">Save and continue later</a>
                    </p>
                    <p class="d-flex align-items-center mb-0">
                        <a @click="onClickBack" href="#" class="p-2 mx-3 d-block back-btn">Back</a>
                        <a @click="onClickNext" class="ml-5 btn btn-primary">Next</a>
                    </p>
                </div>
            </div>

            <div class="card" v-if="currentStep == 4" title="Nutrition">
                <div class="card-header">
                    <h3 class="card-title">Nutrition</h3>
                </div>
                <div class="card-body">
                    <div class="row gx-5">
                        <div class="col-sm-10 col-md-8 col-xl-6">
                            <div class="mb-3 row">
                                <div class="col-5">
                                    <div class="form-label">Admission Height (cm)</div>
                                    <input
                                        type="text"
                                        class="form-control"
                                        name="admissionHeight"
                                        placeholder="Ex. 180"
                                        v-model="step[3].admissionHeight"
                                        :class="{
                                            'is-invalid': v$.step[3].admissionHeight.$invalid,
                                        }"
                                    />
                                    <div
                                        class="invalid-feedback"
                                        :class="{
                                            'd-block': v$.step[3].admissionHeight.$invalid,
                                        }"
                                    >
                                        Invalid feedback
                                    </div>
                                </div>
                                <div class="col-5">
                                    <div class="form-label">Admission Weight (kg)</div>
                                    <input
                                        type="text"
                                        class="form-control"
                                        name="admissionWeight"
                                        placeholder="Ex. 80"
                                        v-model="step[3].admissionWeight"
                                        :class="{
                                            'is-invalid': v$.step[3].admissionWeight.$invalid,
                                        }"
                                    />
                                    <div
                                        class="invalid-feedback"
                                        :class="{
                                            'd-block': v$.step[3].admissionWeight.$invalid,
                                        }"
                                    >
                                        Invalid feedback
                                    </div>
                                </div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Weight/Height appropriate</div>
                                <label v-for="option in yesNoOptions" class="form-check form-check-inline">
                                    <input class="form-check-input" type="radio" :value="option" v-model="step[3].weightHeightAppropriate" name="weightHeightAppropriate" />
                                    <span class="form-check-label">{{ option }}</span>
                                </label>
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[3].weightHeightAppropriate.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Recent weight gain/loss</div>
                                <label v-for="option in yesNoOptions" class="form-check form-check-inline mb-0">
                                    <input class="form-check-input" type="radio" :value="option" v-model="step[3].recentWeightGainLoss" name="recentWeightGainLoss" />
                                    <span class="form-check-label">{{ option }}</span>
                                </label>
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[3].recentWeightGainLoss.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>

                                <input
                                    type="text"
                                    class="form-control w-75 mt-2"
                                    name="recentWeightGainLossDescription"
                                    placeholder="If yes, describe"
                                    v-model="step[3].recentWeightGainLossDescription"
                                    :class="{'is-invalid': v$.step[3].recentWeightGainLossDescription.$invalid}"
                                />
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[3].recentWeightGainLossDescription.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Oral Health</div>
                                <label v-for="option in goodPoorOptions" class="form-check form-check-inline">
                                    <input class="form-check-input" type="radio" :value="option" v-model="step[3].oralHealth" name="oralHealth" />
                                    <span class="form-check-label">{{ option }}</span>
                                </label>
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[3].oralHealth.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Natural Teeth</div>
                                <div class="form-check form-switch mb-0 ms-1 mt-2">
                                    <input class="form-check-input" type="checkbox" name="naturalTeethUpper" v-model="step[3].naturalTeethUpper" />
                                    <span class="form-check-label">Upper</span>
                                </div>

                                <div class="invalid-feedback" :class="{'d-block': v$.step[3].naturalTeethUpper.$invalid}">Invalid feedback</div>

                                <div class="form-check form-switch mb-0 ms-1 mt-2">
                                    <input class="form-check-input" type="checkbox" name="naturalTeethLower" v-model="step[3].naturalTeethLower" />
                                    <span class="form-check-label">Lower</span>
                                </div>

                                <div class="invalid-feedback" :class="{'d-block': v$.step[3].naturalTeethLower.$invalid}">Invalid feedback</div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Dentures</div>
                                <div class="form-check form-switch mb-0 ms-1 mt-2">
                                    <input class="form-check-input" type="checkbox" name="denturesUpper" v-model="step[3].denturesUpper" />
                                    <span class="form-check-label">Upper</span>
                                </div>

                                <div class="invalid-feedback" :class="{'d-block': v$.step[3].denturesUpper.$invalid}">Invalid feedback</div>

                                <div class="form-check form-switch mb-0 ms-1 mt-2">
                                    <input class="form-check-input" type="checkbox" name="denturesLower" v-model="step[3].denturesLower" />
                                    <span class="form-check-label">Lower</span>
                                </div>

                                <div class="invalid-feedback" :class="{'d-block': v$.step[3].denturesLower.$invalid}">Invalid feedback</div>
                            </div>
                        </div>
                        <div class="col-sm-10 col-md-8 col-xl-6">
                            <div class="mb-3">
                                <div class="form-label">Condition of remaining teeth</div>
                                <input
                                    type="text"
                                    class="form-control w-75"
                                    name="remainingTeethCondition"
                                    placeholder="Desciption"
                                    v-model="step[3].remainingTeethCondition"
                                    :class="{'is-invalid': v$.step[3].remainingTeethCondition.$invalid}"
                                />
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[3].remainingTeethCondition.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Last dental examination</div>
                                <input
                                    type="date"
                                    class="form-control w-75"
                                    name="date-of-birth"
                                    placeholder=""
                                    v-model="step[3].lastDentalExamination"
                                    :class="{'is-invalid': v$.step[3].lastDentalExamination.$invalid}"
                                />
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[3].lastDentalExamination.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Dentist, if known</div>
                                <input
                                    type="text"
                                    class="form-control w-75"
                                    name="dentistIfKnown"
                                    placeholder="Name"
                                    v-model="step[3].dentistIfKnown"
                                    :class="{'is-invalid': v$.step[3].dentistIfKnown.$invalid}"
                                />
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[3].dentistIfKnown.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Swallowing difficulties</div>
                                <label v-for="option in yesNoOptions" class="form-check form-check-inline mb-0">
                                    <input class="form-check-input" type="radio" :value="option" v-model="step[3].swallowingDifficulties" name="swallowingDifficulties" />
                                    <span class="form-check-label">{{ option }}</span>
                                </label>
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[3].swallowingDifficulties.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>

                                <input
                                    type="text"
                                    class="form-control w-75 mt-2"
                                    name="swallowingDifficultiesDescription"
                                    placeholder="If yes, describe"
                                    v-model="step[3].swallowingDifficultiesDescription"
                                    :class="{'is-invalid': v$.step[3].swallowingDifficultiesDescription.$invalid}"
                                />
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[3].swallowingDifficultiesDescription.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Other Comments</div>
                                <textarea
                                    rows="4"
                                    class="form-control w-75"
                                    placeholder="Comments"
                                    value=""
                                    spellcheck="false"
                                    name="otherCommentsNutrition"
                                    v-model="step[3].otherCommentsNutrition"
                                    :class="{
                                        'is-invalid': v$.step[3].otherCommentsNutrition.$invalid,
                                    }"
                                ></textarea>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[3].otherCommentsNutrition.$invalid}">Invalid feedback</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="card-footer">
                    <p class="m-0 text-muted">
                        <a href="#" class="save-btn">Save and continue later</a>
                    </p>
                    <p class="d-flex align-items-center mb-0">
                        <a @click="onClickBack" href="#" class="p-2 mx-3 d-block back-btn">Back</a>
                        <a @click="onClickNext" class="ml-5 btn btn-primary">Next</a>
                    </p>
                </div>
            </div>

            <div class="card" v-if="currentStep == 5" title="Elimination">
                <div class="card-header">
                    <h3 class="card-title">Elimination</h3>
                </div>

                <div class="card-body">
                    <div class="row gx-5">
                        <div class="col-sm-10 col-md-8 col-xl-6">
                            <div class="mb-3">
                                <div class="form-label">Admission Urinalysis</div>
                                <label v-for="option in yesNoOptions" class="form-check form-check-inline">
                                    <input class="form-check-input" type="radio" :value="option" v-model="step[4].admissionUrinalysis" name="admissionUrinalysis" />
                                    <span class="form-check-label">{{ option }}</span>
                                </label>
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[4].admissionUrinalysis.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Urinary Continent</div>
                                <label v-for="option in yesNoOptions" class="form-check form-check-inline mb-0">
                                    <input class="form-check-input" type="radio" :value="option" v-model="step[4].urinaryContinent" name="urinaryContinent" />
                                    <span class="form-check-label">{{ option }}</span>
                                </label>
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[4].urinaryContinent.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>

                                <input
                                    type="text"
                                    class="form-control w-75 mt-2"
                                    name="urinaryContinentPattern"
                                    placeholder="If incontinent, please explain pattern"
                                    v-model="step[4].urinaryContinentPattern"
                                    :class="{'is-invalid': v$.step[4].urinaryContinentPattern.$invalid}"
                                />
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[4].urinaryContinentPattern.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Faecal Continent</div>
                                <label v-for="option in yesNoOptions" class="form-check form-check-inline mb-0">
                                    <input class="form-check-input" type="radio" :value="option" v-model="step[4].faecalContinent" name="faecalContinent" />
                                    <span class="form-check-label">{{ option }}</span>
                                </label>
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[4].faecalContinent.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>

                                <input
                                    type="text"
                                    class="form-control w-75 mt-2"
                                    name="faecalContinentPattern"
                                    placeholder="If incontinent, please explain pattern"
                                    v-model="step[4].faecalContinentPattern"
                                    :class="{'is-invalid': v$.step[4].faecalContinentPattern.$invalid}"
                                />
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[4].faecalContinentPattern.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Previous Continence Programme</div>
                                <label v-for="option in yesNoOptions" class="form-check form-check-inline mb-0">
                                    <input class="form-check-input" type="radio" :value="option" v-model="step[4].previousContinence" name="previousContinence" />
                                    <span class="form-check-label">{{ option }}</span>
                                </label>
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[4].previousContinence.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>

                                <input
                                    type="text"
                                    class="form-control w-75 mt-2"
                                    name="previousContinenceDescription"
                                    placeholder="Describe"
                                    v-model="step[4].previousContinenceDescription"
                                    :class="{'is-invalid': v$.step[4].previousContinenceDescription.$invalid}"
                                />
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[4].previousContinenceDescription.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">History of UTI</div>
                                <label v-for="option in yesNoOptions" class="form-check form-check-inline mb-0">
                                    <input class="form-check-input" type="radio" :value="option" v-model="step[4].historyOfUTI" name="historyOfUTI" />
                                    <span class="form-check-label">{{ option }}</span>
                                </label>
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[4].historyOfUTI.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>

                                <input
                                    type="date"
                                    class="form-control mt-2 w-75"
                                    name="historyOfUTILastInfection"
                                    placeholder=""
                                    v-model="step[4].historyOfUTILastInfection"
                                    :class="{'is-invalid': v$.step[4].historyOfUTILastInfection.$invalid}"
                                />
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[4].historyOfUTILastInfection.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>
                        </div>

                        <div class="col-sm-10 col-md-8 col-xl-6">
                            <div class="mb-3">
                                <div class="form-label">Frequency and pattern of voiding urine</div>
                                <input
                                    type="text"
                                    class="form-control w-75"
                                    name="voidingUrineDaytimePattern"
                                    placeholder="Daytime Pattern"
                                    v-model="step[4].voidingUrineDaytimePattern"
                                    :class="{'is-invalid': v$.step[4].voidingUrineDaytimePattern.$invalid}"
                                />
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[4].voidingUrineDaytimePattern.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                                <input
                                    type="text"
                                    class="form-control w-75 mt-2"
                                    name="voidingUrineNighttimePattern"
                                    placeholder="Night time Pattern"
                                    v-model="step[4].voidingUrineNighttimePattern"
                                    :class="{'is-invalid': v$.step[4].voidingUrineNighttimePattern.$invalid}"
                                />
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[4].voidingUrineNighttimePattern.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Frequency and pattern of bowel routine</div>
                                <input
                                    type="text"
                                    class="form-control w-75"
                                    name="bowelRoutineDaytimePattern"
                                    placeholder="Daytime Pattern"
                                    v-model="step[4].bowelRoutineDaytimePattern"
                                    :class="{'is-invalid': v$.step[4].bowelRoutineDaytimePattern.$invalid}"
                                />
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[4].bowelRoutineDaytimePattern.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                                <input
                                    type="text"
                                    class="form-control w-75 mt-2"
                                    name="bowelRoutineNighttimePattern"
                                    placeholder="Night time Pattern"
                                    v-model="step[4].bowelRoutineNighttimePattern"
                                    :class="{'is-invalid': v$.step[4].bowelRoutineNighttimePattern.$invalid}"
                                />
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[4].bowelRoutineNighttimePattern.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Previous medical/surgical history related to bowel/bladder</div>
                                <label v-for="option in yesNoOptions" class="form-check form-check-inline mb-0">
                                    <input class="form-check-input" type="radio" :value="option" v-model="step[4].bowelRelatedMedicalHistory" name="bowelRelatedMedicalHistory" />
                                    <span class="form-check-label">{{ option }}</span>
                                </label>
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[4].bowelRelatedMedicalHistory.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>

                                <input
                                    type="text"
                                    class="form-control w-75 mt-2"
                                    name="bowelRelatedMedicalHistoryDescription"
                                    placeholder="If yes, describe"
                                    v-model="step[4].bowelRelatedMedicalHistoryDescription"
                                    :class="{'is-invalid': v$.step[4].bowelRelatedMedicalHistoryDescription.$invalid}"
                                />
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[4].bowelRelatedMedicalHistoryDescription.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Further Assessment Required</div>
                                <label v-for="option in yesNoOptions" class="form-check form-check-inline">
                                    <input class="form-check-input" type="radio" :value="option" v-model="step[4].furtherAssessmentRequired" name="furtherAssessmentRequired" />
                                    <span class="form-check-label">{{ option }}</span>
                                </label>
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[4].furtherAssessmentRequired.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Other Comments</div>
                                <textarea
                                    rows="4"
                                    class="form-control w-75"
                                    placeholder="Comments"
                                    value=""
                                    spellcheck="false"
                                    name="otherCommentsElimination"
                                    v-model="step[4].otherCommentsElimination"
                                    :class="{
                                        'is-invalid': v$.step[4].otherCommentsElimination.$invalid,
                                    }"
                                ></textarea>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[4].otherCommentsElimination.$invalid}">Invalid feedback</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="card-footer">
                    <p class="m-0 text-muted">
                        <a href="#" class="save-btn">Save and continue later</a>
                    </p>
                    <p class="d-flex align-items-center mb-0">
                        <a @click="onClickBack" href="#" class="p-2 mx-3 d-block back-btn">Back</a>
                        <a @click="onClickNext" class="ml-5 btn btn-primary">Next</a>
                    </p>
                </div>
            </div>

            <div class="card" v-if="currentStep == 6" title="Perception">
                <div class="card-header">
                    <h3 class="card-title">Perception</h3>
                </div>

                <div class="card-body">
                    <div class="row gx-5">
                        <div class="col-sm-10 col-md-8 col-xl-6">
                            <div class="mb-3">
                                <div class="form-label">Primary Language</div>
                                <input
                                    type="text"
                                    class="form-control w-75 mt-1"
                                    name="primaryLanguage"
                                    placeholder="Primary Language"
                                    v-model="step[5].primaryLanguage"
                                    :class="{'is-invalid': v$.step[5].primaryLanguage.$invalid}"
                                />
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[5].primaryLanguage.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Secondary Language</div>
                                <input
                                    type="text"
                                    class="form-control w-75 mt-1"
                                    name="secondaryLanguage"
                                    placeholder="Secondary Language"
                                    v-model="step[5].secondaryLanguage"
                                    :class="{'is-invalid': v$.step[5].secondaryLanguage.$invalid}"
                                />
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[5].secondaryLanguage.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Understand English</div>
                                <label v-for="option in yesNoOptions" class="form-check form-check-inline">
                                    <input class="form-check-input" type="radio" :value="option" v-model="step[5].understandEnglish" name="understandEnglish" />
                                    <span class="form-check-label">{{ option }}</span>
                                </label>
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[5].understandEnglish.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Speech</div>
                                <label v-for="option in speechOptions" class="form-check form-check-inline">
                                    <input class="form-check-input" type="radio" :value="option" v-model="step[5].speech" name="speech" />
                                    <span class="form-check-label">{{ option }}</span>
                                </label>
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[5].speech.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Rate of Speech</div>
                                <label v-for="option in rateOfSpeechOptions" class="form-check form-check-inline mb-0">
                                    <input class="form-check-input" type="radio" :value="option" v-model="step[5].rateOfSpeech" name="rateOfSpeech" />
                                    <span class="form-check-label">{{ option }}</span>
                                </label>
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[5].rateOfSpeech.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Coherent</div>
                                <label v-for="option in yesNoOptions" class="form-check form-check-inline">
                                    <input class="form-check-input" type="radio" :value="option" v-model="step[5].coherent" name="coherent" />
                                    <span class="form-check-label">{{ option }}</span>
                                </label>
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[5].coherent.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Unusual words pattern</div>
                                <label v-for="option in yesNoOptions" class="form-check form-check-inline">
                                    <input class="form-check-input" type="radio" :value="option" v-model="step[5].unusualWordsPattern" name="unusualWordsPattern" />
                                    <span class="form-check-label">{{ option }}</span>
                                </label>
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[5].unusualWordsPattern.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>
                        </div>

                        <div class="col-sm-10 col-md-8 col-xl-6">
                            <div class="mb-3">
                                <div class="form-label">Hallucinations</div>
                                <label v-for="option in yesNoOptions" class="form-check form-check-inline mb-0">
                                    <input class="form-check-input" type="radio" :value="option" v-model="step[5].hallucinations" name="hallucinations" />
                                    <span class="form-check-label">{{ option }}</span>
                                </label>
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[5].hallucinations.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>

                                <input
                                    type="text"
                                    class="form-control w-75 mt-2"
                                    name="hallucinationsDescription"
                                    placeholder="Describe"
                                    v-model="step[5].hallucinationsDescription"
                                    :class="{'is-invalid': v$.step[5].hallucinationsDescription.$invalid}"
                                />
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[5].hallucinationsDescription.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Delusions</div>
                                <label v-for="option in yesNoOptions" class="form-check form-check-inline mb-0">
                                    <input class="form-check-input" type="radio" :value="option" v-model="step[5].delusions" name="delusions" />
                                    <span class="form-check-label">{{ option }}</span>
                                </label>
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[5].delusions.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>

                                <input
                                    type="text"
                                    class="form-control w-75 mt-2"
                                    name="delusionsDescription"
                                    placeholder="Describe"
                                    v-model="step[5].delusionsDescription"
                                    :class="{'is-invalid': v$.step[5].delusionsDescription.$invalid}"
                                />
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[5].delusionsDescription.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Sudden change in topic</div>
                                <label v-for="option in yesNoOptions" class="form-check form-check-inline mb-0">
                                    <input class="form-check-input" type="radio" :value="option" v-model="step[5].suddenChangeInTopic" name="suddenChangeInTopic" />
                                    <span class="form-check-label">{{ option }}</span>
                                </label>
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[5].suddenChangeInTopic.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Other Comments</div>
                                <textarea
                                    rows="4"
                                    class="form-control w-75"
                                    placeholder="Comments"
                                    value=""
                                    spellcheck="false"
                                    name="otherCommentsPerception"
                                    v-model="step[5].otherCommentsPerception"
                                    :class="{
                                        'is-invalid': v$.step[5].otherCommentsPerception.$invalid,
                                    }"
                                ></textarea>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[5].otherCommentsPerception.$invalid}">Invalid feedback</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="card-footer">
                    <p class="m-0 text-muted">
                        <a href="#" class="save-btn">Save and continue later</a>
                    </p>
                    <p class="d-flex align-items-center mb-0">
                        <a @click="onClickBack" href="#" class="p-2 mx-3 d-block back-btn">Back</a>
                        <a @click="onClickNext" class="ml-5 btn btn-primary">Next</a>
                    </p>
                </div>
            </div>

            <div class="card" v-if="currentStep == 7" title="Hearing">
                <div class="card-header">
                    <h3 class="card-title">Hearing</h3>
                </div>

                <div class="card-body">
                    <div class="row gx-5">
                        <div class="col-sm-10 col-md-8 col-xl-6">
                            <div class="mb-3">
                                <div class="form-label">Left Ear</div>
                                <label v-for="option in goodPoorFairOptions" class="form-check form-check-inline">
                                    <input class="form-check-input" type="radio" :value="option" v-model="step[6].leftEar" name="leftEar" />
                                    <span class="form-check-label">{{ option }}</span>
                                </label>
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[6].leftEar.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Right Ear</div>
                                <label v-for="option in goodPoorFairOptions" class="form-check form-check-inline">
                                    <input class="form-check-input" type="radio" :value="option" v-model="step[6].rightEar" name="rightEar" />
                                    <span class="form-check-label">{{ option }}</span>
                                </label>
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[6].rightEar.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Hearing aid</div>
                                <div class="d-flex align-items-center">
                                    <select
                                        class="form-select w-75"
                                        name="hearingAid"
                                        v-model="step[6].hearingAid"
                                        :class="{
                                            'is-invalid': v$.step[6].hearingAid.$invalid,
                                        }"
                                    >
                                        <option v-for="option in noneLeftRightBothOptions" :value="option">
                                            {{ option }}
                                        </option>
                                    </select>
                                </div>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[6].hearingAid.$invalid}">Invalid feedback</div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Wax build up</div>
                                <div class="d-flex align-items-center">
                                    <select
                                        class="form-select w-75"
                                        name="waxBuildUp"
                                        v-model="step[6].waxBuildUp"
                                        :class="{
                                            'is-invalid': v$.step[6].waxBuildUp.$invalid,
                                        }"
                                    >
                                        <option v-for="option in noneLeftRightBothOptions" :value="option">
                                            {{ option }}
                                        </option>
                                    </select>
                                </div>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[6].waxBuildUp.$invalid}">Invalid feedback</div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Needs hearing assessment</div>
                                <label v-for="option in yesNoOptions" class="form-check form-check-inline">
                                    <input class="form-check-input" type="radio" :value="option" v-model="step[6].needsHearingAssessment" name="needsHearingAssessment" />
                                    <span class="form-check-label">{{ option }}</span>
                                </label>
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[6].needsHearingAssessment.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>
                        </div>

                        <div class="col-sm-10 col-md-8 col-xl-6">
                            <div class="mb-3">
                                <div class="form-label">Other Comments</div>
                                <textarea
                                    rows="4"
                                    class="form-control w-75"
                                    placeholder="Comments"
                                    value=""
                                    spellcheck="false"
                                    name="otherCommentsHearing"
                                    v-model="step[6].otherCommentsHearing"
                                    :class="{
                                        'is-invalid': v$.step[6].otherCommentsHearing.$invalid,
                                    }"
                                ></textarea>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[6].otherCommentsHearing.$invalid}">Invalid feedback</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="card-footer">
                    <p class="m-0 text-muted">
                        <a href="#" class="save-btn">Save and continue later</a>
                    </p>
                    <p class="d-flex align-items-center mb-0">
                        <a @click="onClickBack" href="#" class="p-2 mx-3 d-block back-btn">Back</a>
                        <a @click="onClickNext" class="ml-5 btn btn-primary">Next</a>
                    </p>
                </div>
            </div>

            <div class="card" v-if="currentStep == 8" title="Sight">
                <div class="card-header">
                    <h3 class="card-title">Sight</h3>
                </div>

                <div class="card-body">
                    <div class="row gx-5">
                        <div class="col-sm-10 col-md-8 col-xl-6">
                            <div class="mb-3">
                                <div class="form-label">Left Eye</div>
                                <div class="d-flex align-items-center">
                                    <select
                                        class="form-select w-75"
                                        name="leftEye"
                                        v-model="step[7].leftEye"
                                        :class="{
                                            'is-invalid': v$.step[7].leftEye.$invalid,
                                        }"
                                    >
                                        <option v-for="option in sightOptions" :value="option">
                                            {{ option }}
                                        </option>
                                    </select>
                                </div>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[7].leftEye.$invalid}">Invalid feedback</div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Right Eye</div>
                                <div class="d-flex align-items-center">
                                    <select
                                        class="form-select w-75"
                                        name="rightEye"
                                        v-model="step[7].rightEye"
                                        :class="{
                                            'is-invalid': v$.step[7].rightEye.$invalid,
                                        }"
                                    >
                                        <option v-for="option in sightOptions" :value="option">
                                            {{ option }}
                                        </option>
                                    </select>
                                </div>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[7].rightEye.$invalid}">Invalid feedback</div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Wears Spectacles</div>
                                <label v-for="option in yesNoOptions" class="form-check form-check-inline mb-0">
                                    <input class="form-check-input" type="radio" :value="option" v-model="step[7].wearsSpectacles" name="wearsSpectacles" />
                                    <span class="form-check-label">{{ option }}</span>
                                </label>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[7].wearsSpectacles.$invalid}">Invalid feedback</div>

                                <input
                                    type="text"
                                    class="form-control w-75 mt-2"
                                    name="wearsSpectaclesReason"
                                    placeholder="If yes, describe"
                                    v-model="step[7].wearsSpectaclesReason"
                                    :class="{'is-invalid': v$.step[7].wearsSpectaclesReason.$invalid}"
                                />
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[7].wearsSpectaclesReason.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Eye Drops</div>
                                <label v-for="option in yesNoOptions" class="form-check form-check-inline mb-0">
                                    <input class="form-check-input" type="radio" :value="option" v-model="step[7].eyeDrops" name="eyeDrops" />
                                    <span class="form-check-label">{{ option }}</span>
                                </label>
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[7].eyeDrops.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>

                                <input
                                    type="text"
                                    class="form-control w-75 mt-2"
                                    name="eyeDropsDetails"
                                    placeholder="If yes, specify"
                                    v-model="step[7].eyeDropsDetails"
                                    :class="{'is-invalid': v$.step[7].eyeDropsDetails.$invalid}"
                                />
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[7].eyeDropsDetails.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>
                        </div>

                        <div class="col-sm-10 col-md-8 col-xl-6">
                            <div class="mb-3">
                                <div class="form-label">Other Comments</div>
                                <textarea
                                    rows="4"
                                    class="form-control w-75"
                                    placeholder="Comments"
                                    value=""
                                    spellcheck="false"
                                    name="otherCommentsSight"
                                    v-model="step[7].otherCommentsSight"
                                    :class="{
                                        'is-invalid': v$.step[7].otherCommentsSight.$invalid,
                                    }"
                                ></textarea>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[7].otherCommentsSight.$invalid}">Invalid feedback</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="card-footer">
                    <p class="m-0 text-muted">
                        <a href="#" class="save-btn">Save and continue later</a>
                    </p>
                    <p class="d-flex align-items-center mb-0">
                        <a @click="onClickBack" href="#" class="p-2 mx-3 d-block back-btn">Back</a>
                        <a @click="onClickNext" class="ml-5 btn btn-primary">Next</a>
                    </p>
                </div>
            </div>

            <div class="card" v-if="currentStep == 9" title="Mental Ability">
                <div class="card-header">
                    <h3 class="card-title">Mental Ability (Insight/Confusion)</h3>
                </div>

                <div class="card-body">
                    <div class="row gx-5">
                        <div class="col-sm-10 col-md-8 col-xl-6">
                            <div class="mb-3">
                                <div class="form-label">Does resident have mental capacity to make decisions</div>
                                <label v-for="option in yesNoOptions" class="form-check form-check-inline mb-0">
                                    <input class="form-check-input" type="radio" :value="option" v-model="step[8].mentalCapacity" name="mentalCapacity" />
                                    <span class="form-check-label">{{ option }}</span>
                                </label>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[8].mentalCapacity.$invalid}">Invalid feedback</div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Alert/responsive/bright</div>
                                <label v-for="option in alertOptions" class="form-check form-check-inline mb-0">
                                    <input class="form-check-input" type="radio" :value="option" v-model="step[8].alertResponsiveBright" name="alertResponsiveBright" />
                                    <span class="form-check-label">{{ option }}</span>
                                </label>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[8].alertResponsiveBright.$invalid}">Invalid feedback</div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Able to assist in planning care</div>
                                <label v-for="option in yesNoOptions" class="form-check form-check-inline mb-0">
                                    <input class="form-check-input" type="radio" :value="option" v-model="step[8].ableToAssistInPlanningCare" name="ableToAssistInPlanningCare" />
                                    <span class="form-check-label">{{ option }}</span>
                                </label>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[8].ableToAssistInPlanningCare.$invalid}">Invalid feedback</div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Decrease in thought and concentration</div>
                                <label v-for="option in mildModerateSevereOptions" class="form-check form-check-inline mb-0">
                                    <input class="form-check-input" type="radio" :value="option" v-model="step[8].decreaseThoughtConcentration" name="decreaseThoughtConcentration" />
                                    <span class="form-check-label">{{ option }}</span>
                                </label>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[8].decreaseThoughtConcentration.$invalid}">Invalid feedback</div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Confusion</div>
                                <label v-for="option in yesNoOptions" class="form-check form-check-inline mb-0">
                                    <input class="form-check-input" type="radio" :value="option" v-model="step[8].confusion" name="confusion" />
                                    <span class="form-check-label">{{ option }}</span>
                                </label>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[8].confusion.$invalid}">Invalid feedback</div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Short term memory</div>
                                <label v-for="option in goodPoorFairOptions" class="form-check form-check-inline mb-0">
                                    <input class="form-check-input" type="radio" :value="option" v-model="step[8].shortTermMemory" name="shortTermMemory" />
                                    <span class="form-check-label">{{ option }}</span>
                                </label>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[8].shortTermMemory.$invalid}">Invalid feedback</div>
                            </div>
                        </div>

                        <div class="col-sm-10 col-md-8 col-xl-6">
                            <div class="mb-3">
                                <div class="form-label">Long term memory</div>
                                <label v-for="option in goodPoorFairOptions" class="form-check form-check-inline mb-0">
                                    <input class="form-check-input" type="radio" :value="option" v-model="step[8].longTermMemory" name="longTermMemory" />
                                    <span class="form-check-label">{{ option }}</span>
                                </label>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[8].longTermMemory.$invalid}">Invalid feedback</div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Other Comments</div>
                                <textarea
                                    rows="4"
                                    class="form-control w-75"
                                    placeholder="Comments"
                                    value=""
                                    spellcheck="false"
                                    name="otherCommentsMentalAbility"
                                    v-model="step[8].otherCommentsMentalAbility"
                                    :class="{
                                        'is-invalid': v$.step[8].otherCommentsMentalAbility.$invalid,
                                    }"
                                ></textarea>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[8].otherCommentsMentalAbility.$invalid}">Invalid feedback</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="card-footer">
                    <p class="m-0 text-muted">
                        <a href="#" class="save-btn">Save and continue later</a>
                    </p>
                    <p class="d-flex align-items-center mb-0">
                        <a @click="onClickBack" href="#" class="p-2 mx-3 d-block back-btn">Back</a>
                        <a @click="onClickNext" class="ml-5 btn btn-primary">Next</a>
                    </p>
                </div>
            </div>

            <div class="card" v-if="currentStep == 10" title="Behavioural Assessment">
                <div class="card-header">
                    <h3 class="card-title">Behavioural Assessment</h3>
                </div>

                <div class="card-body">
                    <div class="row gx-5">
                        <div class="col-sm-10 col-md-8 col-xl-6">
                            <div class="mb-3">
                                <div class="form-label">Does the client have insight to his diagnosis</div>
                                <label v-for="option in yesNoOptions" class="form-check form-check-inline mb-0">
                                    <input class="form-check-input" type="radio" :value="option" v-model="step[9].insightToDiagnosis" name="insightToDiagnosis" />
                                    <span class="form-check-label">{{ option }}</span>
                                </label>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[9].insightToDiagnosis.$invalid}">Invalid feedback</div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Episodes of wandering or getting lost</div>
                                <label v-for="option in yesNoOptions" class="form-check form-check-inline mb-0">
                                    <input class="form-check-input" type="radio" :value="option" v-model="step[9].episodesOfWandering" name="episodesOfWandering" />
                                    <span class="form-check-label">{{ option }}</span>
                                </label>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[9].episodesOfWandering.$invalid}">Invalid feedback</div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Situation client finds challenging or threatening</div>
                                <label v-for="option in yesNoOptions" class="form-check form-check-inline mb-0">
                                    <input class="form-check-input" type="radio" :value="option" v-model="step[9].situationClientFindsChallenging" name="situationClientFindsChallenging" />
                                    <span class="form-check-label">{{ option }}</span>
                                </label>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[9].situationClientFindsChallenging.$invalid}">Invalid feedback</div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Premorbid personality</div>
                                <label v-for="option in yesNoOptions" class="form-check form-check-inline mb-0">
                                    <input class="form-check-input" type="radio" :value="option" v-model="step[9].premorbidPersonality" name="premorbidPersonality" />
                                    <span class="form-check-label">{{ option }}</span>
                                </label>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[9].premorbidPersonality.$invalid}">Invalid feedback</div>

                                <input
                                    type="text"
                                    class="form-control w-75 mt-2"
                                    name="premorbidPersonalityDescription"
                                    placeholder="Describe"
                                    v-model="step[9].premorbidPersonalityDescription"
                                    :class="{'is-invalid': v$.step[9].premorbidPersonalityDescription.$invalid}"
                                />
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[9].premorbidPersonalityDescription.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Sources of conflict</div>
                                <label v-for="option in yesNoOptions" class="form-check form-check-inline mb-0">
                                    <input class="form-check-input" type="radio" :value="option" v-model="step[9].sourcesOfConflict" name="sourcesOfConflict" />
                                    <span class="form-check-label">{{ option }}</span>
                                </label>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[9].sourcesOfConflict.$invalid}">Invalid feedback</div>

                                <input
                                    type="text"
                                    class="form-control w-75 mt-2"
                                    name="sourcesOfConflictDescription"
                                    placeholder="Describe"
                                    v-model="step[9].sourcesOfConflictDescription"
                                    :class="{'is-invalid': v$.step[9].sourcesOfConflictDescription.$invalid}"
                                />
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[9].sourcesOfConflictDescription.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>
                        </div>

                        <div class="col-sm-10 col-md-8 col-xl-6">
                            <div class="mb-3">
                                <div class="form-label">Suggestions to assist diffuse conflict or divert client</div>
                                <input
                                    type="text"
                                    class="form-control w-75 mt-2"
                                    name="suggestionsToDiffuseConflict"
                                    placeholder="Describe"
                                    v-model="step[9].suggestionsToDiffuseConflict"
                                    :class="{'is-invalid': v$.step[9].suggestionsToDiffuseConflict.$invalid}"
                                />
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[9].suggestionsToDiffuseConflict.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Further behaviour assessment required</div>
                                <label v-for="option in yesNoOptions" class="form-check form-check-inline mb-0">
                                    <input class="form-check-input" type="radio" :value="option" v-model="step[9].furtherBehaviourAssessmentRequired" name="furtherBehaviourAssessmentRequired" />
                                    <span class="form-check-label">{{ option }}</span>
                                </label>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[9].furtherBehaviourAssessmentRequired.$invalid}">Invalid feedback</div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Other Comments</div>
                                <textarea
                                    rows="4"
                                    class="form-control w-75"
                                    placeholder="Comments"
                                    value=""
                                    spellcheck="false"
                                    name="otherCommentsBehaviouralAssessment"
                                    v-model="step[9].otherCommentsBehaviouralAssessment"
                                    :class="{
                                        'is-invalid': v$.step[9].otherCommentsBehaviouralAssessment.$invalid,
                                    }"
                                ></textarea>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[9].otherCommentsBehaviouralAssessment.$invalid}">Invalid feedback</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="card-footer">
                    <p class="m-0 text-muted">
                        <a href="#" class="save-btn">Save and continue later</a>
                    </p>
                    <p class="d-flex align-items-center mb-0">
                        <a @click="onClickBack" href="#" class="p-2 mx-3 d-block back-btn">Back</a>
                        <a @click="onClickNext" class="ml-5 btn btn-primary">Next</a>
                    </p>
                </div>
            </div>

            <div class="card" v-if="currentStep == 11" title="Depression Assessment">
                <div class="card-header">
                    <h3 class="card-title">Depression Assessment</h3>
                </div>

                <div class="card-body">
                    <div class="row gx-5">
                        <div class="col-sm-10 col-md-8 col-xl-6">
                            <div class="mb-3">
                                <div class="form-label">Is the client happy most of the time?</div>
                                <label v-for="option in yesNoOptions" class="form-check form-check-inline mb-0">
                                    <input class="form-check-input" type="radio" :value="option" v-model="step[10].happyMostOfTheTime" name="happyMostOfTheTime" />
                                    <span class="form-check-label">{{ option }}</span>
                                </label>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[10].happyMostOfTheTime.$invalid}">Invalid feedback</div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Does the client express a feeling of helpfulness</div>
                                <label v-for="option in yesNoOptions" class="form-check form-check-inline mb-0">
                                    <input class="form-check-input" type="radio" :value="option" v-model="step[10].expressAFeelingOfHelpfulness" name="expressAFeelingOfHelpfulness" />
                                    <span class="form-check-label">{{ option }}</span>
                                </label>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[10].expressAFeelingOfHelpfulness.$invalid}">Invalid feedback</div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Does the client cry often?</div>
                                <label v-for="option in yesNoOptions" class="form-check form-check-inline mb-0">
                                    <input class="form-check-input" type="radio" :value="option" v-model="step[10].cryOften" name="cryOften" />
                                    <span class="form-check-label">{{ option }}</span>
                                </label>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[10].cryOften.$invalid}">Invalid feedback</div>

                                <input
                                    type="text"
                                    class="form-control w-75 mt-2"
                                    name="cryOftenDescription"
                                    placeholder="Describe"
                                    v-model="step[10].cryOftenDescription"
                                    :class="{'is-invalid': v$.step[10].cryOftenDescription.$invalid}"
                                />
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[10].cryOftenDescription.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Does client express a wish of self-harm</div>
                                <label v-for="option in yesNoOptions" class="form-check form-check-inline mb-0">
                                    <input class="form-check-input" type="radio" :value="option" v-model="step[10].expressAWishOfSelfHarm" name="expressAWishOfSelfHarm" />
                                    <span class="form-check-label">{{ option }}</span>
                                </label>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[10].expressAWishOfSelfHarm.$invalid}">Invalid feedback</div>

                                <input
                                    type="text"
                                    class="form-control w-75 mt-2"
                                    name="expressAWishOfSelfHarmDescription"
                                    placeholder="Describe"
                                    v-model="step[10].expressAWishOfSelfHarmDescription"
                                    :class="{'is-invalid': v$.step[10].expressAWishOfSelfHarmDescription.$invalid}"
                                />
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[10].expressAWishOfSelfHarmDescription.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>
                        </div>

                        <div class="col-sm-10 col-md-8 col-xl-6">
                            <div class="mb-3">
                                <div class="form-label">Other Comments</div>
                                <textarea
                                    rows="4"
                                    class="form-control w-75"
                                    placeholder="Comments"
                                    value=""
                                    spellcheck="false"
                                    name="otherCommentsDepressionAssessment"
                                    v-model="step[10].otherCommentsDepressionAssessment"
                                    :class="{
                                        'is-invalid': v$.step[10].otherCommentsDepressionAssessment.$invalid,
                                    }"
                                ></textarea>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[10].otherCommentsDepressionAssessment.$invalid}">Invalid feedback</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="card-footer">
                    <p class="m-0 text-muted">
                        <a href="#" class="save-btn">Save and continue later</a>
                    </p>
                    <p class="d-flex align-items-center mb-0">
                        <a @click="onClickBack" href="#" class="p-2 mx-3 d-block back-btn">Back</a>
                        <a @click="onClickNext" class="ml-5 btn btn-primary">Next</a>
                    </p>
                </div>
            </div>

            <div class="card" v-if="currentStep == 12" title="Pain Assessment">
                <div class="card-header">
                    <h3 class="card-title">Pain Assessment</h3>
                </div>

                <div class="card-body">
                    <div class="row gx-5">
                        <div class="col-sm-10 col-md-8 col-xl-6">
                            <div class="mb-3">
                                <div class="form-label">Does the client have pain</div>
                                <label v-for="option in noneAcuteChronicOptions" class="form-check form-check-inline mb-0">
                                    <input class="form-check-input" type="radio" :value="option" v-model="step[11].clientHavePain" name="clientHavePain" />
                                    <span class="form-check-label">{{ option }}</span>
                                </label>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[11].clientHavePain.$invalid}">Invalid feedback</div>
                            </div>

                            <div class="mb-3 row">
                                <div class="col-4">
                                    <div class="form-label">When</div>
                                    <input
                                        type="date"
                                        class="form-control w-100"
                                        name="clientHavePainWhen"
                                        placeholder=""
                                        v-model="step[11].clientHavePainWhen"
                                        :class="{'is-invalid': v$.step[11].clientHavePainWhen.$invalid}"
                                    />
                                    <div
                                        class="invalid-feedback"
                                        :class="{
                                            'd-block': v$.step[11].clientHavePainWhen.$invalid,
                                        }"
                                    >
                                        Invalid feedback
                                    </div>
                                </div>
                                <div class="col-4">
                                    <div class="form-label">Where</div>
                                    <input
                                        type="text"
                                        class="form-control w-100"
                                        name="clientHavePainWhere"
                                        placeholder="Where"
                                        v-model="step[11].clientHavePainWhere"
                                        :class="{'is-invalid': v$.step[11].clientHavePainWhere.$invalid}"
                                    />
                                    <div
                                        class="invalid-feedback"
                                        :class="{
                                            'd-block': v$.step[11].clientHavePainWhere.$invalid,
                                        }"
                                    >
                                        Invalid feedback
                                    </div>
                                </div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Possible cause of pain</div>
                                <input
                                    type="text"
                                    class="form-control w-75"
                                    name="possibleCauseOfPain"
                                    placeholder="Possible cause of pain"
                                    v-model="step[11].possibleCauseOfPain"
                                    :class="{'is-invalid': v$.step[11].possibleCauseOfPain.$invalid}"
                                />
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[11].possibleCauseOfPain.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Signs & Symptoms</div>
                                <input
                                    type="text"
                                    class="form-control w-75"
                                    name="signsOfPain"
                                    placeholder="Possible cause of pain"
                                    v-model="step[11].signsOfPain"
                                    :class="{'is-invalid': v$.step[11].signsOfPain.$invalid}"
                                />
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[11].signsOfPain.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Type of Pain</div>
                                <input
                                    type="text"
                                    class="form-control w-75"
                                    name="typeOfPain"
                                    placeholder="Type of pain"
                                    v-model="step[11].typeOfPain"
                                    :class="{'is-invalid': v$.step[11].typeOfPain.$invalid}"
                                />
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[11].typeOfPain.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>
                        </div>

                        <div class="col-sm-10 col-md-8 col-xl-6">
                            <div class="mb-3">
                                <div class="form-label">Intensity of Pain</div>

                                <input
                                    type="text"
                                    class="form-control w-75 mt-2"
                                    name="intensityOfPain"
                                    placeholder="Intensity of pain"
                                    v-model="step[11].intensityOfPain"
                                    :class="{'is-invalid': v$.step[11].intensityOfPain.$invalid}"
                                />
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[11].intensityOfPain.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Pain Medications</div>
                                <label v-for="option in yesNoOptions" class="form-check form-check-inline mb-0">
                                    <input class="form-check-input" type="radio" :value="option" v-model="step[11].painMedication" name="painMedication" />
                                    <span class="form-check-label">{{ option }}</span>
                                </label>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[11].painMedication.$invalid}">Invalid feedback</div>

                                <input
                                    type="text"
                                    class="form-control w-75 mt-2"
                                    name="painMedicationMedicines"
                                    placeholder="If yes, list down the medicine(s)"
                                    v-model="step[11].painMedicationMedicines"
                                    :class="{'is-invalid': v$.step[11].painMedicationMedicines.$invalid}"
                                />
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[11].painMedicationMedicines.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Pain assessment required</div>
                                <label v-for="option in yesNoOptions" class="form-check form-check-inline mb-0">
                                    <input class="form-check-input" type="radio" :value="option" v-model="step[11].painAssessmentRequired" name="painAssessmentRequired" />
                                    <span class="form-check-label">{{ option }}</span>
                                </label>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[11].painAssessmentRequired.$invalid}">Invalid feedback</div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Other Comments</div>
                                <textarea
                                    rows="4"
                                    class="form-control w-75"
                                    placeholder="Comments"
                                    value=""
                                    spellcheck="false"
                                    name="otherCommentsPainAssessment"
                                    v-model="step[11].otherCommentsPainAssessment"
                                    :class="{
                                        'is-invalid': v$.step[11].otherCommentsPainAssessment.$invalid,
                                    }"
                                ></textarea>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[11].otherCommentsPainAssessment.$invalid}">Invalid feedback</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="card-footer">
                    <p class="m-0 text-muted">
                        <a href="#" class="save-btn">Save and continue later</a>
                    </p>
                    <p class="d-flex align-items-center mb-0">
                        <a @click="onClickBack" href="#" class="p-2 mx-3 d-block back-btn">Back</a>
                        <a @click="onClickNext" class="ml-5 btn btn-primary">Next</a>
                    </p>
                </div>
            </div>

            <div class="card" v-if="currentStep == 13" title="Level of Personal Ability / Independence">
                <div class="card-header">
                    <h3 class="card-title">Level of Personal Ability / Independence</h3>
                </div>

                <div class="card-body">
                    <div class="row gx-5">
                        <div class="col-sm-10 col-md-8 col-xl-6">
                            <div class="mb-3">
                                <div class="form-label">Showering</div>
                                <select
                                    class="form-select w-75"
                                    name="showering"
                                    v-model="step[12].showering"
                                    :class="{
                                        'is-invalid': v$.step[12].showering.$invalid,
                                    }"
                                >
                                    <option v-for="option in abilityOptions" :value="option">
                                        {{ option }}
                                    </option>
                                </select>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[12].showering.$invalid}">Invalid feedback</div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Washing</div>
                                <select
                                    class="form-select w-75"
                                    name="washing"
                                    v-model="step[12].washing"
                                    :class="{
                                        'is-invalid': v$.step[12].washing.$invalid,
                                    }"
                                >
                                    <option v-for="option in abilityOptions" :value="option">
                                        {{ option }}
                                    </option>
                                </select>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[12].washing.$invalid}">Invalid feedback</div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Drying Self</div>
                                <select
                                    class="form-select w-75"
                                    name="dryingSelf"
                                    v-model="step[12].dryingSelf"
                                    :class="{
                                        'is-invalid': v$.step[12].dryingSelf.$invalid,
                                    }"
                                >
                                    <option v-for="option in abilityOptions" :value="option">
                                        {{ option }}
                                    </option>
                                </select>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[12].dryingSelf.$invalid}">Invalid feedback</div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Dressing Undressing</div>
                                <select
                                    class="form-select w-75"
                                    name="dressingUndressing"
                                    v-model="step[12].dressingUndressing"
                                    :class="{
                                        'is-invalid': v$.step[12].dressingUndressing.$invalid,
                                    }"
                                >
                                    <option v-for="option in abilityOptions" :value="option">
                                        {{ option }}
                                    </option>
                                </select>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[12].dressingUndressing.$invalid}">Invalid feedback</div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Toileting</div>
                                <select
                                    class="form-select w-75"
                                    name="toileting"
                                    v-model="step[12].toileting"
                                    :class="{
                                        'is-invalid': v$.step[12].toileting.$invalid,
                                    }"
                                >
                                    <option v-for="option in abilityOptions" :value="option">
                                        {{ option }}
                                    </option>
                                </select>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[12].toileting.$invalid}">Invalid feedback</div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Cleaning teeth/dentures</div>
                                <select
                                    class="form-select w-75"
                                    name="cleaningTeeth"
                                    v-model="step[12].cleaningTeeth"
                                    :class="{
                                        'is-invalid': v$.step[12].cleaningTeeth.$invalid,
                                    }"
                                >
                                    <option v-for="option in abilityOptions" :value="option">
                                        {{ option }}
                                    </option>
                                </select>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[12].cleaningTeeth.$invalid}">Invalid feedback</div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Hair care & Grooming</div>
                                <select
                                    class="form-select w-75"
                                    name="hairCareGrooming"
                                    v-model="step[12].hairCareGrooming"
                                    :class="{
                                        'is-invalid': v$.step[12].hairCareGrooming.$invalid,
                                    }"
                                >
                                    <option v-for="option in abilityOptions" :value="option">
                                        {{ option }}
                                    </option>
                                </select>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[12].hairCareGrooming.$invalid}">Invalid feedback</div>
                            </div>
                        </div>
                        <div class="col-sm-10 col-md-8 col-xl-6">
                            <div class="mb-3">
                                <div class="form-label">Shaving</div>
                                <select
                                    class="form-select w-75"
                                    name="shaving"
                                    v-model="step[12].shaving"
                                    :class="{
                                        'is-invalid': v$.step[12].shaving.$invalid,
                                    }"
                                >
                                    <option v-for="option in abilityOptions" :value="option">
                                        {{ option }}
                                    </option>
                                </select>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[12].shaving.$invalid}">Invalid feedback</div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Cutting nails</div>
                                <select
                                    class="form-select w-75"
                                    name="cuttingNails"
                                    v-model="step[12].cuttingNails"
                                    :class="{
                                        'is-invalid': v$.step[12].cuttingNails.$invalid,
                                    }"
                                >
                                    <option v-for="option in abilityOptions" :value="option">
                                        {{ option }}
                                    </option>
                                </select>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[12].cuttingNails.$invalid}">Invalid feedback</div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Choosing clothes</div>
                                <select
                                    class="form-select w-75"
                                    name="choosingClothes"
                                    v-model="step[12].choosingClothes"
                                    :class="{
                                        'is-invalid': v$.step[12].choosingClothes.$invalid,
                                    }"
                                >
                                    <option v-for="option in abilityOptions" :value="option">
                                        {{ option }}
                                    </option>
                                </select>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[12].choosingClothes.$invalid}">Invalid feedback</div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Putting on footwear</div>
                                <select
                                    class="form-select w-75"
                                    name="puttingOnFootwear"
                                    v-model="step[12].puttingOnFootwear"
                                    :class="{
                                        'is-invalid': v$.step[12].puttingOnFootwear.$invalid,
                                    }"
                                >
                                    <option v-for="option in abilityOptions" :value="option">
                                        {{ option }}
                                    </option>
                                </select>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[12].puttingOnFootwear.$invalid}">Invalid feedback</div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Standing</div>
                                <select
                                    class="form-select w-75"
                                    name="standing"
                                    v-model="step[12].standing"
                                    :class="{
                                        'is-invalid': v$.step[12].standing.$invalid,
                                    }"
                                >
                                    <option v-for="option in abilityOptions" :value="option">
                                        {{ option }}
                                    </option>
                                </select>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[12].standing.$invalid}">Invalid feedback</div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Transferring</div>
                                <select
                                    class="form-select w-75"
                                    name="transferring"
                                    v-model="step[12].transferring"
                                    :class="{
                                        'is-invalid': v$.step[12].transferring.$invalid,
                                    }"
                                >
                                    <option v-for="option in abilityOptions" :value="option" class="py-2">
                                        {{ option }}
                                    </option>
                                </select>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[12].transferring.$invalid}">Invalid feedback</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="card-footer">
                    <p class="m-0 text-muted">
                        <a href="#" class="save-btn">Save and continue later</a>
                    </p>
                    <p class="d-flex align-items-center mb-0">
                        <a @click="onClickBack" href="#" class="p-2 mx-3 d-block back-btn">Back</a>
                        <a @click="onClickNext" class="ml-5 btn btn-primary">Next</a>
                    </p>
                </div>
            </div>

            <div class="card" v-if="currentStep == 14" title="Skin Integrity">
                <div class="card-header">
                    <h3 class="card-title">Skin Integrity</h3>
                </div>

                <div class="card-body">
                    <div class="row gx-5">
                        <div class="col-sm-10 col-md-8 col-xl-6">
                            <div class="mb-3 row">
                                <div class="col-12">
                                    <div class="form-label">Dry skin</div>
                                </div>
                                <div class="col-4">
                                    <select
                                        class="form-select w-100"
                                        name="drySkin"
                                        v-model="step[13].drySkin"
                                        :class="{
                                            'is-invalid': v$.step[13].drySkin.$invalid,
                                        }"
                                    >
                                        <option v-for="option in skinIntegrityOptions" :value="option">
                                            {{ option }}
                                        </option>
                                    </select>
                                    <div class="invalid-feedback" :class="{'d-block': v$.step[13].drySkin.$invalid}">Invalid feedback</div>
                                </div>
                                <div class="col-6">
                                    <input
                                        type="text"
                                        class="form-control w-100"
                                        name="drySkinDescription"
                                        placeholder="Describe"
                                        v-model="step[13].drySkinDescription"
                                        :class="{'is-invalid': v$.step[13].drySkinDescription.$invalid}"
                                    />
                                    <div
                                        class="invalid-feedback"
                                        :class="{
                                            'd-block': v$.step[13].drySkinDescription.$invalid,
                                        }"
                                    >
                                        Invalid feedback
                                    </div>
                                </div>
                            </div>

                            <div class="mb-3 row">
                                <div class="col-12">
                                    <div class="form-label">Red areas</div>
                                </div>
                                <div class="col-4">
                                    <select
                                        class="form-select w-100"
                                        name="redAreas"
                                        v-model="step[13].redAreas"
                                        :class="{
                                            'is-invalid': v$.step[13].redAreas.$invalid,
                                        }"
                                    >
                                        <option v-for="option in skinIntegrityOptions" :value="option">
                                            {{ option }}
                                        </option>
                                    </select>
                                    <div class="invalid-feedback" :class="{'d-block': v$.step[13].redAreas.$invalid}">Invalid feedback</div>
                                </div>
                                <div class="col-6">
                                    <input
                                        type="text"
                                        class="form-control w-100"
                                        name="redAreasDescription"
                                        placeholder="Describe"
                                        v-model="step[13].redAreasDescription"
                                        :class="{'is-invalid': v$.step[13].redAreasDescription.$invalid}"
                                    />
                                    <div
                                        class="invalid-feedback"
                                        :class="{
                                            'd-block': v$.step[13].redAreasDescription.$invalid,
                                        }"
                                    >
                                        Invalid feedback
                                    </div>
                                </div>
                            </div>

                            <div class="mb-3 row">
                                <div class="col-12">
                                    <div class="form-label">Bruise areas</div>
                                </div>
                                <div class="col-4">
                                    <select
                                        class="form-select w-100"
                                        name="bruiseAreas"
                                        v-model="step[13].bruiseAreas"
                                        :class="{
                                            'is-invalid': v$.step[13].bruiseAreas.$invalid,
                                        }"
                                    >
                                        <option v-for="option in skinIntegrityOptions" :value="option">
                                            {{ option }}
                                        </option>
                                    </select>
                                    <div class="invalid-feedback" :class="{'d-block': v$.step[13].bruiseAreas.$invalid}">Invalid feedback</div>
                                </div>
                                <div class="col-6">
                                    <input
                                        type="text"
                                        class="form-control w-100"
                                        name="bruiseAreasDescription"
                                        placeholder="Describe"
                                        v-model="step[13].bruiseAreasDescription"
                                        :class="{'is-invalid': v$.step[13].bruiseAreasDescription.$invalid}"
                                    />
                                    <div
                                        class="invalid-feedback"
                                        :class="{
                                            'd-block': v$.step[13].bruiseAreasDescription.$invalid,
                                        }"
                                    >
                                        Invalid feedback
                                    </div>
                                </div>
                            </div>

                            <div class="mb-3 row">
                                <div class="col-12">
                                    <div class="form-label">Scars/cut areas</div>
                                </div>
                                <div class="col-4">
                                    <select
                                        class="form-select w-100"
                                        name="scarAreas"
                                        v-model="step[13].scarAreas"
                                        :class="{
                                            'is-invalid': v$.step[13].scarAreas.$invalid,
                                        }"
                                    >
                                        <option v-for="option in skinIntegrityOptions" :value="option">
                                            {{ option }}
                                        </option>
                                    </select>
                                    <div class="invalid-feedback" :class="{'d-block': v$.step[13].scarAreas.$invalid}">Invalid feedback</div>
                                </div>
                                <div class="col-6">
                                    <input
                                        type="text"
                                        class="form-control w-100"
                                        name="scarAreasDescription"
                                        placeholder="Describe"
                                        v-model="step[13].scarAreasDescription"
                                        :class="{'is-invalid': v$.step[13].scarAreasDescription.$invalid}"
                                    />
                                    <div
                                        class="invalid-feedback"
                                        :class="{
                                            'd-block': v$.step[13].scarAreasDescription.$invalid,
                                        }"
                                    >
                                        Invalid feedback
                                    </div>
                                </div>
                            </div>

                            <div class="mb-3 row">
                                <div class="col-12">
                                    <div class="form-label">Rashes</div>
                                </div>
                                <div class="col-4">
                                    <select
                                        class="form-select w-100"
                                        name="rashes"
                                        v-model="step[13].rashes"
                                        :class="{
                                            'is-invalid': v$.step[13].rashes.$invalid,
                                        }"
                                    >
                                        <option v-for="option in skinIntegrityOptions" :value="option">
                                            {{ option }}
                                        </option>
                                    </select>
                                    <div class="invalid-feedback" :class="{'d-block': v$.step[13].rashes.$invalid}">Invalid feedback</div>
                                </div>
                                <div class="col-6">
                                    <input
                                        type="text"
                                        class="form-control w-100"
                                        name="rashesDescription"
                                        placeholder="Describe"
                                        v-model="step[13].rashesDescription"
                                        :class="{'is-invalid': v$.step[13].rashesDescription.$invalid}"
                                    />
                                    <div
                                        class="invalid-feedback"
                                        :class="{
                                            'd-block': v$.step[13].rashesDescription.$invalid,
                                        }"
                                    >
                                        Invalid feedback
                                    </div>
                                </div>
                            </div>

                            <div class="mb-3 row">
                                <div class="col-12">
                                    <div class="form-label">Skin Tears</div>
                                </div>
                                <div class="col-4">
                                    <select
                                        class="form-select w-100"
                                        name="skinTears"
                                        v-model="step[13].skinTears"
                                        :class="{
                                            'is-invalid': v$.step[13].skinTears.$invalid,
                                        }"
                                    >
                                        <option v-for="option in skinIntegrityOptions" :value="option">
                                            {{ option }}
                                        </option>
                                    </select>
                                    <div class="invalid-feedback" :class="{'d-block': v$.step[13].skinTears.$invalid}">Invalid feedback</div>
                                </div>
                                <div class="col-6">
                                    <input
                                        type="text"
                                        class="form-control w-100"
                                        name="skinTearsDescription"
                                        placeholder="Describe"
                                        v-model="step[13].skinTearsDescription"
                                        :class="{'is-invalid': v$.step[13].skinTearsDescription.$invalid}"
                                    />
                                    <div
                                        class="invalid-feedback"
                                        :class="{
                                            'd-block': v$.step[13].skinTearsDescription.$invalid,
                                        }"
                                    >
                                        Invalid feedback
                                    </div>
                                </div>
                            </div>

                            <div class="mb-3 row">
                                <div class="col-12">
                                    <div class="form-label">Pressure related ulcers</div>
                                </div>
                                <div class="col-4">
                                    <select
                                        class="form-select w-100"
                                        name="pressureRelatedUlcers"
                                        v-model="step[13].pressureRelatedUlcers"
                                        :class="{
                                            'is-invalid': v$.step[13].pressureRelatedUlcers.$invalid,
                                        }"
                                    >
                                        <option v-for="option in skinIntegrityOptions" :value="option">
                                            {{ option }}
                                        </option>
                                    </select>
                                    <div class="invalid-feedback" :class="{'d-block': v$.step[13].pressureRelatedUlcers.$invalid}">Invalid feedback</div>
                                </div>
                                <div class="col-6">
                                    <input
                                        type="text"
                                        class="form-control w-100"
                                        name="pressureRelatedUlcersDescription"
                                        placeholder="Describe"
                                        v-model="step[13].pressureRelatedUlcersDescription"
                                        :class="{'is-invalid': v$.step[13].pressureRelatedUlcersDescription.$invalid}"
                                    />
                                    <div
                                        class="invalid-feedback"
                                        :class="{
                                            'd-block': v$.step[13].pressureRelatedUlcersDescription.$invalid,
                                        }"
                                    >
                                        Invalid feedback
                                    </div>
                                </div>
                            </div>

                            <div class="mb-3 row">
                                <div class="col-12">
                                    <div class="form-label">Circulatory related ulcers</div>
                                </div>
                                <div class="col-4">
                                    <select
                                        class="form-select w-100"
                                        name="circulatoryRelatedUlcers"
                                        v-model="step[13].circulatoryRelatedUlcers"
                                        :class="{
                                            'is-invalid': v$.step[13].circulatoryRelatedUlcers.$invalid,
                                        }"
                                    >
                                        <option v-for="option in skinIntegrityOptions" :value="option">
                                            {{ option }}
                                        </option>
                                    </select>
                                    <div class="invalid-feedback" :class="{'d-block': v$.step[13].circulatoryRelatedUlcers.$invalid}">Invalid feedback</div>
                                </div>
                                <div class="col-6">
                                    <input
                                        type="text"
                                        class="form-control w-100"
                                        name="circulatoryRelatedUlcersDescription"
                                        placeholder="Describe"
                                        v-model="step[13].circulatoryRelatedUlcersDescription"
                                        :class="{'is-invalid': v$.step[13].circulatoryRelatedUlcersDescription.$invalid}"
                                    />
                                    <div
                                        class="invalid-feedback"
                                        :class="{
                                            'd-block': v$.step[13].circulatoryRelatedUlcersDescription.$invalid,
                                        }"
                                    >
                                        Invalid feedback
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="col-sm-10 col-md-8 col-xl-6">
                            <div class="mb-3">
                                <div class="form-label">Admission WATERLOW score</div>
                                <textarea
                                    rows="4"
                                    class="form-control w-75"
                                    placeholder="WATERLOW score"
                                    value=""
                                    spellcheck="false"
                                    name="admissionWaterlowScore"
                                    v-model="step[13].admissionWaterlowScore"
                                    :class="{
                                        'is-invalid': v$.step[13].admissionWaterlowScore.$invalid,
                                    }"
                                ></textarea>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[13].admissionWaterlowScore.$invalid}">Invalid feedback</div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Other Comments</div>
                                <textarea
                                    rows="4"
                                    class="form-control w-75"
                                    placeholder="Comments"
                                    value=""
                                    spellcheck="false"
                                    name="otherCommentsSkinIntegrity"
                                    v-model="step[13].otherCommentsSkinIntegrity"
                                    :class="{
                                        'is-invalid': v$.step[13].otherCommentsSkinIntegrity.$invalid,
                                    }"
                                ></textarea>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[13].otherCommentsSkinIntegrity.$invalid}">Invalid feedback</div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Illustration</div>
                                <img class="mt-3" src="/img/skin-integrity-illustration.jpg" />
                            </div>
                        </div>
                    </div>
                </div>
                <div class="card-footer">
                    <p class="m-0 text-muted">
                        <a href="#" class="save-btn">Save and continue later</a>
                    </p>
                    <p class="d-flex align-items-center mb-0">
                        <a @click="onClickBack" href="#" class="p-2 mx-3 d-block back-btn">Back</a>
                        <a @click="onClickNext" class="ml-5 btn btn-primary">Next</a>
                    </p>
                </div>
            </div>

            <div class="card" v-if="currentStep == 15" title="Sexuality / Privacy">
                <div class="card-header">
                    <h3 class="card-title">Sexuality / Privacy</h3>
                </div>

                <div class="card-body">
                    <div class="row gx-5">
                        <div class="col-sm-10 col-md-8 col-xl-6">
                            <div class="mb-3">
                                <div class="form-label">How is sexuality expressed?</div>
                                <input
                                    type="text"
                                    class="form-control w-75 mt-2"
                                    name="howIsSexualityExpressed"
                                    placeholder="How is sexuality expressed?"
                                    v-model="step[14].howIsSexualityExpressed"
                                    :class="{'is-invalid': v$.step[14].howIsSexualityExpressed.$invalid}"
                                />
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[14].howIsSexualityExpressed.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Interest in sexual activity?</div>
                                <input
                                    type="text"
                                    class="form-control w-75 mt-2"
                                    name="interestInSexualActivity"
                                    placeholder="Interest in sexual activity?"
                                    v-model="step[14].interestInSexualActivity"
                                    :class="{'is-invalid': v$.step[14].interestInSexualActivity.$invalid}"
                                />
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[14].interestInSexualActivity.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Response to touch</div>
                                <input
                                    type="text"
                                    class="form-control w-75 mt-2"
                                    name="responseToTouch"
                                    placeholder="Response to touch"
                                    v-model="step[14].responseToTouch"
                                    :class="{'is-invalid': v$.step[14].responseToTouch.$invalid}"
                                />
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[14].responseToTouch.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Medical conditions/soreness related to sexual organs</div>
                                <input
                                    type="text"
                                    class="form-control w-75 mt-2"
                                    name="sexualOrgansMedicalConditions"
                                    placeholder="Medical conditions/soreness related to sexual organs"
                                    v-model="step[14].sexualOrgansMedicalConditions"
                                    :class="{'is-invalid': v$.step[14].sexualOrgansMedicalConditions.$invalid}"
                                />
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[14].sexualOrgansMedicalConditions.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>
                        </div>
                        <div class="col-sm-10 col-md-8 col-xl-6">
                            <div class="mb-3 row">
                                <div class="col-12">
                                    <div class="form-label">Previous examination</div>
                                </div>
                                <div class="col-12 col-md-3 mt-1">
                                    <div class="form-check form-switch mb-0 ms-1 mt-2">
                                        <input class="form-check-input" type="checkbox" name="previousExaminationBreast" v-model="step[14].previousExaminationBreast" />
                                        <span class="form-check-label small">Breasts</span>
                                    </div>

                                    <div class="invalid-feedback" :class="{'d-block': v$.step[14].previousExaminationBreast.$invalid}">Invalid feedback</div>
                                </div>
                                <div class="col-12 col-md-7 mt-2 mt-md-1">
                                    <input
                                        type="date"
                                        class="form-control w-75"
                                        name="previousExaminationBreastDate"
                                        placeholder=""
                                        v-model="step[14].previousExaminationBreastDate"
                                        :class="{'is-invalid': v$.step[14].previousExaminationBreastDate.$invalid}"
                                    />

                                    <div class="invalid-feedback" :class="{'d-block': v$.step[14].previousExaminationBreastDate.$invalid}">Invalid feedback</div>
                                </div>

                                <div class="col-12 col-md-3 mt-1">
                                    <div class="form-check form-switch mb-0 ms-1 mt-2">
                                        <input class="form-check-input" type="checkbox" name="previousExaminationCervix" v-model="step[14].previousExaminationCervix" />
                                        <span class="form-check-label small">Cervix</span>
                                    </div>

                                    <div class="invalid-feedback" :class="{'d-block': v$.step[14].previousExaminationCervix.$invalid}">Invalid feedback</div>
                                </div>
                                <div class="col-12 col-md-7 mt-2 mt-md-1">
                                    <input
                                        type="date"
                                        class="form-control w-75"
                                        name="previousExaminationCervixDate"
                                        placeholder=""
                                        v-model="step[14].previousExaminationCervixDate"
                                        :class="{'is-invalid': v$.step[14].previousExaminationCervixDate.$invalid}"
                                    />

                                    <div class="invalid-feedback" :class="{'d-block': v$.step[14].previousExaminationCervixDate.$invalid}">Invalid feedback</div>
                                </div>

                                <div class="col-12 col-md-3 mt-1">
                                    <div class="form-check form-switch mb-0 ms-1 mt-2">
                                        <input class="form-check-input" type="checkbox" name="previousExaminationProstate" v-model="step[14].previousExaminationProstate" />
                                        <span class="form-check-label small">Prostate</span>
                                    </div>

                                    <div class="invalid-feedback" :class="{'d-block': v$.step[14].previousExaminationProstate.$invalid}">Invalid feedback</div>
                                </div>
                                <div class="col-12 col-md-7 mt-2 mt-md-1">
                                    <input
                                        type="date"
                                        class="form-control w-75"
                                        name="previousExaminationProstateDate"
                                        placeholder=""
                                        v-model="step[14].previousExaminationProstateDate"
                                        :class="{'is-invalid': v$.step[14].previousExaminationProstateDate.$invalid}"
                                    />

                                    <div class="invalid-feedback" :class="{'d-block': v$.step[14].previousExaminationProstateDate.$invalid}">Invalid feedback</div>
                                </div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Other Comments</div>
                                <textarea
                                    rows="4"
                                    class="form-control w-75"
                                    placeholder="Comments"
                                    value=""
                                    spellcheck="false"
                                    name="otherCommentsSexualityPrivacy"
                                    v-model="step[14].otherCommentsSexualityPrivacy"
                                    :class="{
                                        'is-invalid': v$.step[14].otherCommentsSexualityPrivacy.$invalid,
                                    }"
                                ></textarea>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[14].otherCommentsSexualityPrivacy.$invalid}">Invalid feedback</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="card-footer">
                    <p class="m-0 text-muted">
                        <a href="#" class="save-btn">Save and continue later</a>
                    </p>
                    <p class="d-flex align-items-center mb-0">
                        <a @click="onClickBack" href="#" class="p-2 mx-3 d-block back-btn">Back</a>
                        <a @click="onClickNext" class="ml-5 btn btn-primary">Next</a>
                    </p>
                </div>
            </div>

            <div class="card" v-if="currentStep == 16" title="Values / Beliefs">
                <div class="card-header">
                    <h3 class="card-title">Values / Beliefs</h3>
                </div>

                <div class="card-body">
                    <div class="row gx-5">
                        <div class="col-sm-10 col-md-8 col-xl-6">
                            <div class="mb-3">
                                <div class="form-label">Religious/church beliefs</div>
                                <input
                                    type="text"
                                    class="form-control w-75 mt-2"
                                    name="religiousChurchBeliefs"
                                    placeholder="Religious/church beliefs"
                                    v-model="step[15].religiousChurchBeliefs"
                                    :class="{'is-invalid': v$.step[15].religiousChurchBeliefs.$invalid}"
                                />
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[15].religiousChurchBeliefs.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Wishes to continue beliefs</div>
                                <label v-for="option in yesNoOptions" class="form-check form-check-inline">
                                    <input class="form-check-input" type="radio" :value="option" v-model="step[15].wishesToContinueBeliefs" name="wishesToContinueBeliefs" />
                                    <span class="form-check-label">{{ option }}</span>
                                </label>
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[15].wishesToContinueBeliefs.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Cultural Influences</div>
                                <input
                                    type="text"
                                    class="form-control w-75 mt-2"
                                    name="culturalInfluences"
                                    placeholder="Cultural Influences"
                                    v-model="step[15].culturalInfluences"
                                    :class="{'is-invalid': v$.step[15].culturalInfluences.$invalid}"
                                />
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[15].culturalInfluences.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Preference for Burial/Cremation</div>
                                <input
                                    type="text"
                                    class="form-control w-75 mt-2"
                                    name="burialCremationPreference"
                                    placeholder="Cultural Influences"
                                    v-model="step[15].burialCremationPreference"
                                    :class="{'is-invalid': v$.step[15].burialCremationPreference.$invalid}"
                                />
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[15].burialCremationPreference.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>
                            <div class="mb-3">
                                <div class="form-label">Other Comments</div>
                                <textarea
                                    rows="4"
                                    class="form-control w-75"
                                    placeholder="Comments"
                                    value=""
                                    spellcheck="false"
                                    name="otherCommentsValuesBeliefs"
                                    v-model="step[15].otherCommentsValuesBeliefs"
                                    :class="{
                                        'is-invalid': v$.step[15].otherCommentsValuesBeliefs.$invalid,
                                    }"
                                ></textarea>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[15].otherCommentsValuesBeliefs.$invalid}">Invalid feedback</div>
                            </div>
                        </div>
                        <div class="col-sm-10 col-md-8 col-xl-6"></div>
                    </div>
                </div>
                <div class="card-footer">
                    <p class="m-0 text-muted">
                        <a href="#" class="save-btn">Save and continue later</a>
                    </p>
                    <p class="d-flex align-items-center mb-0">
                        <a @click="onClickBack" href="#" class="p-2 mx-3 d-block back-btn">Back</a>
                        <a @click="onClickNext" class="ml-5 btn btn-primary">Next</a>
                    </p>
                </div>
            </div>

            <div class="card" v-if="currentStep == 17" title="Orientation to the Facility and Services">
                <div class="card-header">
                    <h3 class="card-title">Orientation to the Facility and Services</h3>
                </div>

                <div class="card-body">
                    <div class="row gx-5">
                        <div class="col-sm-10 col-md-8 col-xl-6">
                            <div class="mb-3">
                                <div class="form-label">Has the resident and their family received and understood the following</div>
                                <div class="form-check form-switch mb-0 ms-1">
                                    <input class="form-check-input" type="checkbox" name="understoodResidentCodeOfRights" v-model="step[16].understoodResidentCodeOfRights" />
                                    <span class="form-check-label">Resident code of rights and responsibilities</span>
                                </div>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[16].understoodResidentCodeOfRights.$invalid}">Invalid feedback</div>

                                <div class="form-check form-switch mb-0 ms-1 mt-2">
                                    <input class="form-check-input" type="checkbox" name="understoodPhilosophyOfTheFacility" v-model="step[16].understoodPhilosophyOfTheFacility" />
                                    <span class="form-check-label">Philosophy of the facility</span>
                                </div>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[16].understoodPhilosophyOfTheFacility.$invalid}">Invalid feedback</div>

                                <div class="form-check form-switch mb-0 ms-1 mt-2">
                                    <input class="form-check-input" type="checkbox" name="understoodComplaintsComplimentsProcedure" v-model="step[16].understoodComplaintsComplimentsProcedure" />
                                    <span class="form-check-label">Complaints / compliments procedure</span>
                                </div>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[16].understoodComplaintsComplimentsProcedure.$invalid}">Invalid feedback</div>

                                <div class="form-check form-switch mb-0 ms-1 mt-2">
                                    <input class="form-check-input" type="checkbox" name="understoodStandardsOfService" v-model="step[16].understoodStandardsOfService" />
                                    <span class="form-check-label">Standards of service</span>
                                </div>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[16].understoodStandardsOfService.$invalid}">Invalid feedback</div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Has the resident been shown the following</div>
                                <div class="form-check form-switch mb-0 ms-1">
                                    <input class="form-check-input" type="checkbox" name="shownCommunalLounge" v-model="step[16].shownCommunalLounge" />
                                    <span class="form-check-label">The communal lounge</span>
                                </div>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[16].shownCommunalLounge.$invalid}">Invalid feedback</div>

                                <div class="form-check form-switch mb-0 ms-1 mt-2">
                                    <input class="form-check-input" type="checkbox" name="shownDiningRoom" v-model="step[16].shownDiningRoom" />
                                    <span class="form-check-label">Dining room</span>
                                </div>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[16].shownDiningRoom.$invalid}">Invalid feedback</div>

                                <div class="form-check form-switch mb-0 ms-1 mt-2">
                                    <input class="form-check-input" type="checkbox" name="shownBathroomFacilities" v-model="step[16].shownBathroomFacilities" />
                                    <span class="form-check-label">Bathroom facilities</span>
                                </div>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[16].shownBathroomFacilities.$invalid}">Invalid feedback</div>

                                <div class="form-check form-switch mb-0 ms-1 mt-2">
                                    <input class="form-check-input" type="checkbox" name="shownNoticeBoard" v-model="step[16].shownNoticeBoard" />
                                    <span class="form-check-label">Notice board</span>
                                </div>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[16].shownNoticeBoard.$invalid}">Invalid feedback</div>

                                <div class="form-check form-switch mb-0 ms-1 mt-2">
                                    <input class="form-check-input" type="checkbox" name="shownRecreationRoom" v-model="step[16].shownRecreationRoom" />
                                    <span class="form-check-label">Recreation room/area</span>
                                </div>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[16].shownRecreationRoom.$invalid}">Invalid feedback</div>

                                <div class="form-check form-switch mb-0 ms-1 mt-2">
                                    <input class="form-check-input" type="checkbox" name="shownManagersOffice" v-model="step[16].shownManagersOffice" />
                                    <span class="form-check-label">The managers/admin office</span>
                                </div>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[16].shownManagersOffice.$invalid}">Invalid feedback</div>
                            </div>
                        </div>
                        <div class="col-sm-10 col-md-8 col-xl-6">
                            <div class="mb-3">
                                <div class="form-label">Other Comments</div>
                                <textarea
                                    rows="4"
                                    class="form-control w-75"
                                    placeholder="Comments"
                                    value=""
                                    spellcheck="false"
                                    name="otherCommentsOrientation"
                                    v-model="step[16].otherCommentsOrientation"
                                    :class="{
                                        'is-invalid': v$.step[16].otherCommentsOrientation.$invalid,
                                    }"
                                ></textarea>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[16].otherCommentsOrientation.$invalid}">Invalid feedback</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="card-footer">
                    <p class="m-0 text-muted">
                        <a href="#" class="save-btn">Save and continue later</a>
                    </p>
                    <p class="d-flex align-items-center mb-0">
                        <a @click="onClickBack" href="#" class="p-2 mx-3 d-block back-btn">Back</a>
                        <a @click="onClickNext" class="ml-5 btn btn-primary">Next</a>
                    </p>
                </div>
            </div>

            <div class="card" v-if="currentStep == 18" title="Orientation to the Facility and Services">
                <div class="card-header">
                    <h3 class="card-title">Orientation to the Facility and Services</h3>
                </div>

                <div class="card-body">
                    <div class="row gx-5">
                        <div class="col-sm-10 col-md-8 col-xl-6">
                            <div class="mb-3">
                                <div class="form-label">RN's Signature</div>
                                <input
                                    type="text"
                                    class="form-control w-75 mt-2"
                                    name="registeredNurseSignature"
                                    placeholder="Enter your full name"
                                    v-model="step[17].registeredNurseSignature"
                                    :class="{'is-invalid': v$.step[17].registeredNurseSignature.$invalid}"
                                />
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[17].registeredNurseSignature.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>
                            <div class="mb-5">
                                <div class="form-label">Date Signed</div>
                                <input type="date" class="form-control w-50" name="dateSigned" placeholder="" v-model="step[17].dateSigned" :class="{'is-invalid': v$.step[17].dateSigned.$invalid}" />

                                <div class="invalid-feedback" :class="{'d-block': v$.step[17].dateSigned.$invalid}">Invalid feedback</div>
                            </div>
                        </div>
                        <div class="col-sm-10 col-md-8 col-xl-6"></div>
                    </div>
                </div>

                <div class="card-footer">
                    <p class="m-0 text-muted">
                        <a href="#" class="save-btn" id="last-save-btn">Save and continue later</a>
                    </p>
                    <p class="d-flex align-items-center mb-0">
                        <a @click="onClickBack" href="#" class="p-2 mx-3 d-block back-btn" id="last-back-btn">Back</a>
                        <a @click="onClickNext" class="ml-5 btn btn-primary" id="submit-btn">Finish</a>
                    </p>
                </div>
            </div>
        </form>
    </div>
</template>

<script>
import {alpha, alphaNum, email, integer, required, requiredIf, minLength, maxLength, minValue, maxValue, helpers} from "@vuelidate/validators";
import {useVuelidate} from "@vuelidate/core";
import Multiselect from "vue-multiselect";

// Custom Validators
const inArray = (array) => (value) => array.includes(value) || !helpers.req(value);
const isDateFormat = (value) => value.match("^$|((?:[0-9]{2})?[0-9]{2})-(1[0-2]|0?[1-9])-(3[01]|[12][0-9]|0?[1-9])") || !helpers.req(value);
const isTimeFormat = (value) => value.match("^$|^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$") || !helpers.req(value) || "";
const isTextWithSpace = (value) => value.match("^[a-zA-Z0-9_ \s\'\"]*$") || !helpers.req(value) || value >= 0;

export default {
    name: "nursingAssessmentCreateComponent",
    props: ["progressBar", "pageTitle", "residentId", "userId", "nursingAssessment", "operation"], // vue can read kebab props as camel cased vars
    components: {Multiselect},
    setup() {
        return {v$: useVuelidate()};
    },
    data() {
        // If copying, assign nursingAssessment object to a variable
        if (this.operation == "store") {
            var nursingAssessmentObject = {};
        } else if (this.operation == "copy") {
            var nursingAssessmentObject = JSON.parse(this.nursingAssessment);
            nursingAssessmentObject["activity_level"] = JSON.parse(nursingAssessmentObject["activity_level"]);
            nursingAssessmentObject["sleep_patterns"] = JSON.parse(nursingAssessmentObject["sleep_patterns"]);
            nursingAssessmentObject["mobility"] = JSON.parse(nursingAssessmentObject["mobility"]);
            nursingAssessmentObject["nutrition"] = JSON.parse(nursingAssessmentObject["nutrition"]);
            nursingAssessmentObject["elimination"] = JSON.parse(nursingAssessmentObject["elimination"]);
            nursingAssessmentObject["perception"] = JSON.parse(nursingAssessmentObject["perception"]);
            nursingAssessmentObject["hearing"] = JSON.parse(nursingAssessmentObject["hearing"]);
            nursingAssessmentObject["sight"] = JSON.parse(nursingAssessmentObject["sight"]);
            nursingAssessmentObject["mental_ability"] = JSON.parse(nursingAssessmentObject["mental_ability"]);
            nursingAssessmentObject["behavioural_assessment"] = JSON.parse(nursingAssessmentObject["behavioural_assessment"]);
            nursingAssessmentObject["depression_assessment"] = JSON.parse(nursingAssessmentObject["depression_assessment"]);
            nursingAssessmentObject["pain_assessment"] = JSON.parse(nursingAssessmentObject["pain_assessment"]);
            nursingAssessmentObject["personal_ability"] = JSON.parse(nursingAssessmentObject["personal_ability"]);
            nursingAssessmentObject["skin_integrity"] = JSON.parse(nursingAssessmentObject["skin_integrity"]);
            nursingAssessmentObject["sexuality_privacy"] = JSON.parse(nursingAssessmentObject["sexuality_privacy"]);
            nursingAssessmentObject["values_beliefs"] = JSON.parse(nursingAssessmentObject["values_beliefs"]);
            nursingAssessmentObject["orientation"] = JSON.parse(nursingAssessmentObject["orientation"]);
            nursingAssessmentObject["registered_nurse_signature"] = JSON.parse(nursingAssessmentObject["registered_nurse_signature"]);
        }

        // Return empty string when object doesn't exist
        // options - array | string
        function returnValueFromObject(fieldName, parent, defaultValue = "", options) {
            // Editing
            if (!_.isEmpty(nursingAssessmentObject)) {
                if (nursingAssessmentObject[parent][fieldName] == null) {
                    return defaultValue;
                } else {
                    return nursingAssessmentObject[parent][fieldName];
                }
            } else {
                return defaultValue;
            }
        }

        return {
            currentStep: 1,
            maxStep: 18,
            percentageComplete: 0,
            errors: 0,
            yesNoOptions: ["Yes", "No"],
            trueFalseOptions: [true, false],
            preferredActivitiesOptions: [
                "Sing Along",
                "Gradening",
                "Walking",
                "Golf Target",
                "Lawn Skittles",
                "Tea Dance",
                "Art & Crafting Session",
                "Bingo",
                "Book Club",
                "Jigsaw",
                "Baking",
                "Sensory Session",
                "Meditation",
                "Photo Sharing",
                "Film Night",
                "Washing Up",
                "Discussion Group",
                "Knitting Circle",
                "Spa Day",
                "Encourage Game Play",
            ],
            interestsHobbiesOptions: [
                "Creative Arts",
                "Reading",
                "Painting",
                "Gardening",
                "Cooking",
                "Photography",
                "Music",
                "Puzzles",
                "Fitness Activities",
                "Flower Arranging",
                "Knitting",
                "Movie Nights",
                "Music and instruments",
                "Pets",
                "walking",
                "Yoga",
            ],
            selectedActivities: "",
            yesNoDontKnowOptions: ["Yes", "No", "Don't Know"],
            goodPoorFairOptions: ["Good", "Poor", "Fair"],
            goodPoorOptions: ["Good", "Poor"],
            abilityOptions: ["Independent", "Requires Supervision", "Dependent"],
            skinIntegrityOptions: ["None", "A", "CO", "E", "F", "H", "HE", "HF", "K", "L", "S", "SH"],
            pillowsMaxCount: 10,
            levelOfRiskMaxCount: 20,
            speechOptions: ["Relevant", "Irrelevant"],
            rateOfSpeechOptions: ["Talks a lot", "Minimal words"],
            noneLeftRightBothOptions: ["None", "Left", "Right", "Both"],
            sightOptions: ["Normal", "Weak", "Partially Blind", "Completely Blind"],
            alertOptions: ["Yes", "No", "Poor"],
            mildModerateSevereOptions: ["None", "Mild", "Moderate", "Severe"],
            noneAcuteChronicOptions: ["None", "Acute", "Chronic"],
            host: document.location.origin,

            step: [
                // Step 1 - Activity Level
                {
                    optionalActivityTime: returnValueFromObject("optionalActivityTime", "activity_level"),
                    preferredActivities: returnValueFromObject("preferredActivities", "activity_level"),
                    interestsHobbies: returnValueFromObject("interestsHobbies", "activity_level"),
                    orientedToTime: returnValueFromObject("orientedToTime", "activity_level", false),
                    orientedToPlace: returnValueFromObject("orientedToPlace", "activity_level", false),
                    recognisesFamily: returnValueFromObject("recognisesFamily", "activity_level", false),
                    recognisesStaff: returnValueFromObject("recognisesStaff", "activity_level", false),
                    understandsConversation: returnValueFromObject("understandsConversation", "activity_level"),
                    attentionSpan: returnValueFromObject("attentionSpan", "activity_level"),
                    otherCommentsActivity: returnValueFromObject("otherCommentsActivity", "activity_level"),
                },
                // Step 2 - Sleep Patterns
                {
                    preferredBedtime: returnValueFromObject("preferredBedtime", "sleep_patterns"),
                    preferredWakingTime: returnValueFromObject("preferredWakingTime", "sleep_patterns"),
                    numberOfPillows: returnValueFromObject("numberOfPillows", "sleep_patterns", "0"),
                    wakesUpAtNightTime: returnValueFromObject("wakesUpAtNightTime", "sleep_patterns"),
                    wakesUpAtNightReason: returnValueFromObject("wakesUpAtNightReason", "sleep_patterns"),
                    dreamsNightmaresFears: returnValueFromObject("dreamsNightmaresFears", "sleep_patterns", "No"),
                    wandersAtNight: returnValueFromObject("wandersAtNight", "sleep_patterns", "No"),
                    sleepAidWarmDrink: returnValueFromObject("sleepAidWarmDrink", "sleep_patterns", false),
                    sleepAidMedication: returnValueFromObject("sleepAidMedication", "sleep_patterns", false),
                    sleepAidRadioMusic: returnValueFromObject("sleepAidRadioMusic", "sleep_patterns", false),
                    daytimeRestsNaps: returnValueFromObject("daytimeRestsNaps", "sleep_patterns", "No"),
                    otherCommentsSleepPatterns: returnValueFromObject("otherCommentsSleepPatterns", "sleep_patterns"),
                },
                // Step 3 - Mobility
                {
                    fullyMobile: returnValueFromObject("fullyMobile", "mobility", "No"),
                    feetAndNailCondition: returnValueFromObject("feetAndNailCondition", "mobility"),
                    limitationsInMovementDescription: returnValueFromObject("limitationsInMovementDescription", "mobility"),
                    podiatryRequired: returnValueFromObject("podiatryRequired", "mobility", "No"),
                    mobilityAidsUsed: returnValueFromObject("mobilityAidsUsed", "mobility"),
                    wandering: returnValueFromObject("wandering", "mobility", "No"),
                    typeOfWanderingDescription: returnValueFromObject("typeOfWanderingDescription", "mobility"),
                    typeOfPreferredFootwear: returnValueFromObject("typeOfPreferredFootwear", "mobility"),
                    typeOfPreferredFootwearSafe: returnValueFromObject("typeOfPreferredFootwearSafe", "mobility"),
                    riskOfFallingBalance: returnValueFromObject("riskOfFallingBalance", "mobility", false),
                    riskOfFallingBalanceComment: returnValueFromObject("riskOfFallingBalanceComment", "mobility"),
                    riskOfFallingLimbStrength: returnValueFromObject("riskOfFallingLimbStrength", "mobility", false),
                    riskOfFallingLimbStrengthComment: returnValueFromObject("riskOfFallingLimbStrengthComment", "mobility"),
                    riskOfFallingMobility: returnValueFromObject("riskOfFallingMobility", "mobility", false),
                    riskOfFallingMobilityComment: returnValueFromObject("riskOfFallingMobilityComment", "mobility"),
                    riskOfFallingUseOfAids: returnValueFromObject("riskOfFallingUseOfAids", "mobility", false),
                    riskOfFallingUseOfAidsComment: returnValueFromObject("riskOfFallingUseOfAidsComment", "mobility"),
                    riskOfFallingSight: returnValueFromObject("riskOfFallingSight", "mobility", false),
                    riskOfFallingSightComment: returnValueFromObject("riskOfFallingSightComment", "mobility"),
                    riskOfFallingMentalState: returnValueFromObject("riskOfFallingMentalState", "mobility", false),
                    riskOfFallingMentalStateComment: returnValueFromObject("riskOfFallingMentalStateComment", "mobility"),
                    // levelOfRisk: returnValueFromObject('levelOfRisk', 'mobility', '0'),
                },
                // Step 4 - Nutrition
                {
                    admissionHeight: returnValueFromObject("admissionHeight", "nutrition"),
                    admissionWeight: returnValueFromObject("admissionWeight", "nutrition"),
                    weightHeightAppropriate: returnValueFromObject("weightHeightAppropriate", "nutrition", "Yes"),
                    recentWeightGainLoss: returnValueFromObject("recentWeightGainLoss", "nutrition", "No"),
                    recentWeightGainLossDescription: returnValueFromObject("recentWeightGainLossDescription", "nutrition"),
                    oralHealth: returnValueFromObject("oralHealth", "nutrition", "Good"),
                    naturalTeethUpper: returnValueFromObject("naturalTeethUpper", "nutrition", false),
                    naturalTeethLower: returnValueFromObject("naturalTeethLower", "nutrition", false),
                    denturesUpper: returnValueFromObject("denturesUpper", "nutrition", false),
                    denturesLower: returnValueFromObject("denturesLower", "nutrition", false),
                    remainingTeethCondition: returnValueFromObject("remainingTeethCondition", "nutrition"),
                    lastDentalExamination: returnValueFromObject("lastDentalExamination", "nutrition"),
                    dentistIfKnown: returnValueFromObject("dentistIfKnown", "nutrition"),
                    swallowingDifficulties: returnValueFromObject("swallowingDifficulties", "nutrition"),
                    swallowingDifficultiesDescription: returnValueFromObject("swallowingDifficultiesDescription", "nutrition"),
                    otherCommentsNutrition: returnValueFromObject("otherCommentsNutrition", "nutrition"),
                },
                // Step 5 - Elimination
                {
                    admissionUrinalysis: returnValueFromObject("admissionUrinalysis", "elimination"),
                    urinaryContinent: returnValueFromObject("urinaryContinent", "elimination"),
                    urinaryContinentPattern: returnValueFromObject("urinaryContinentPattern", "elimination"),
                    faecalContinent: returnValueFromObject("faecalContinent", "elimination"),
                    faecalContinentPattern: returnValueFromObject("faecalContinentPattern", "elimination"),
                    previousContinence: returnValueFromObject("previousContinence", "elimination"),
                    previousContinenceDescription: returnValueFromObject("previousContinenceDescription", "elimination"),
                    historyOfUTI: returnValueFromObject("historyOfUTI", "elimination"),
                    historyOfUTILastInfection: returnValueFromObject("historyOfUTILastInfection", "elimination", "2023-01-01"),
                    voidingUrineDaytimePattern: returnValueFromObject("voidingUrineDaytimePattern", "elimination"),
                    voidingUrineNighttimePattern: returnValueFromObject("voidingUrineNighttimePattern", "elimination"),
                    bowelRoutineDaytimePattern: returnValueFromObject("bowelRoutineDaytimePattern", "elimination"),
                    bowelRoutineNighttimePattern: returnValueFromObject("bowelRoutineNighttimePattern", "elimination"),
                    bowelRelatedMedicalHistory: returnValueFromObject("bowelRelatedMedicalHistory", "elimination"),
                    bowelRelatedMedicalHistoryDescription: returnValueFromObject("bowelRelatedMedicalHistoryDescription", "elimination"),
                    furtherAssessmentRequired: returnValueFromObject("furtherAssessmentRequired", "elimination"),
                    otherCommentsElimination: returnValueFromObject("otherCommentsElimination", "elimination"),
                },
                // Step 6 - Perception
                {
                    primaryLanguage: returnValueFromObject("primaryLanguage", "perception"),
                    secondaryLanguage: returnValueFromObject("secondaryLanguage", "perception"),
                    understandEnglish: returnValueFromObject("understandEnglish", "perception"),
                    speech: returnValueFromObject("speech", "perception"),
                    rateOfSpeech: returnValueFromObject("rateOfSpeech", "perception"),
                    coherent: returnValueFromObject("coherent", "perception"),
                    unusualWordsPattern: returnValueFromObject("unusualWordsPattern", "perception"),
                    hallucinations: returnValueFromObject("hallucinations", "perception"),
                    hallucinationsDescription: returnValueFromObject("hallucinationsDescription", "perception"),
                    delusions: returnValueFromObject("delusions", "perception"),
                    delusionsDescription: returnValueFromObject("delusionsDescription", "perception"),
                    suddenChangeInTopic: returnValueFromObject("suddenChangeInTopic", "perception"),
                    otherCommentsPerception: returnValueFromObject("otherCommentsPerception", "perception"),
                },
                // Step 7 - Hearing
                {
                    leftEar: returnValueFromObject("leftEar", "hearing"),
                    rightEar: returnValueFromObject("rightEar", "hearing"),
                    hearingAid: returnValueFromObject("hearingAid", "hearing", "None"),
                    waxBuildUp: returnValueFromObject("waxBuildUp", "hearing", "None"),
                    needsHearingAssessment: returnValueFromObject("needsHearingAssessment", "hearing"),
                    otherCommentsHearing: returnValueFromObject("otherCommentsHearing", "hearing"),
                },
                // Step 8 - Sight
                {
                    leftEye: returnValueFromObject("leftEye", "sight", "Normal"),
                    rightEye: returnValueFromObject("rightEye", "sight", "Normal"),
                    wearsSpectacles: returnValueFromObject("wearsSpectacles", "sight", "No"),
                    wearsSpectaclesReason: returnValueFromObject("wearsSpectaclesReason", "sight"),
                    eyeDrops: returnValueFromObject("eyeDrops", "sight", "No"),
                    eyeDropsDetails: returnValueFromObject("eyeDropsDetails", "sight"),
                    otherCommentsSight: returnValueFromObject("otherCommentsSight", "sight"),
                },
                // Step 9 - Mental Ability
                {
                    mentalCapacity: returnValueFromObject("mentalCapacity", "mental_ability"),
                    alertResponsiveBright: returnValueFromObject("alertResponsiveBright", "mental_ability"),
                    ableToAssistInPlanningCare: returnValueFromObject("ableToAssistInPlanningCare", "mental_ability"),
                    decreaseThoughtConcentration: returnValueFromObject("decreaseThoughtConcentration", "mental_ability"),
                    confusion: returnValueFromObject("confusion", "mental_ability"),
                    shortTermMemory: returnValueFromObject("shortTermMemory", "mental_ability"),
                    longTermMemory: returnValueFromObject("longTermMemory", "mental_ability"),
                    otherCommentsMentalAbility: returnValueFromObject("otherCommentsMentalAbility", "mental_ability"),
                },
                // Step 10 - Behavioural Assessment
                {
                    insightToDiagnosis: returnValueFromObject("insightToDiagnosis", "behavioural_assessment"),
                    episodesOfWandering: returnValueFromObject("episodesOfWandering", "behavioural_assessment"),
                    situationClientFindsChallenging: returnValueFromObject("situationClientFindsChallenging", "behavioural_assessment"),
                    premorbidPersonality: returnValueFromObject("premorbidPersonality", "behavioural_assessment"),
                    premorbidPersonalityDescription: returnValueFromObject("premorbidPersonalityDescription", "behavioural_assessment"),
                    sourcesOfConflict: returnValueFromObject("sourcesOfConflict", "behavioural_assessment"),
                    sourcesOfConflictDescription: returnValueFromObject("sourcesOfConflictDescription", "behavioural_assessment"),
                    suggestionsToDiffuseConflict: returnValueFromObject("suggestionsToDiffuseConflict", "behavioural_assessment"),
                    furtherBehaviourAssessmentRequired: returnValueFromObject("furtherBehaviourAssessmentRequired", "behavioural_assessment"),
                    otherCommentsBehaviouralAssessment: returnValueFromObject("otherCommentsBehaviouralAssessment", "behavioural_assessment"),
                },
                // Step 11 - Depression Assessment
                {
                    happyMostOfTheTime: returnValueFromObject("happyMostOfTheTime", "depression_assessment"),
                    expressAFeelingOfHelpfulness: returnValueFromObject("expressAFeelingOfHelpfulness", "depression_assessment"),
                    cryOften: returnValueFromObject("cryOften", "depression_assessment"),
                    cryOftenDescription: returnValueFromObject("cryOftenDescription", "depression_assessment"),
                    expressAWishOfSelfHarm: returnValueFromObject("expressAWishOfSelfHarm", "depression_assessment"),
                    expressAWishOfSelfHarmDescription: returnValueFromObject("expressAWishOfSelfHarmDescription", "depression_assessment"),
                    otherCommentsDepressionAssessment: returnValueFromObject("otherCommentsDepressionAssessment", "depression_assessment"),
                },
                // Step 12 - Pain Assessment
                {
                    clientHavePain: returnValueFromObject("clientHavePain", "pain_assessment"),
                    clientHavePainWhen: returnValueFromObject("clientHavePainWhen", "pain_assessment"),
                    clientHavePainWhere: returnValueFromObject("clientHavePainWhere", "pain_assessment"),
                    possibleCauseOfPain: returnValueFromObject("possibleCauseOfPain", "pain_assessment"),
                    signsOfPain: returnValueFromObject("signsOfPain", "pain_assessment"),
                    typeOfPain: returnValueFromObject("typeOfPain", "pain_assessment"),
                    intensityOfPain: returnValueFromObject("intensityOfPain", "pain_assessment"),
                    painMedication: returnValueFromObject("painMedication", "pain_assessment"),
                    painMedicationMedicines: returnValueFromObject("painMedicationMedicines", "pain_assessment"),
                    painAssessmentRequired: returnValueFromObject("painAssessmentRequired", "pain_assessment"),
                    otherCommentsPainAssessment: returnValueFromObject("otherCommentsPainAssessment", "pain_assessment"),
                },
                // Step 13 - Personal Ability
                {
                    showering: returnValueFromObject("showering", "personal_ability"),
                    washing: returnValueFromObject("washing", "personal_ability"),
                    dryingSelf: returnValueFromObject("dryingSelf", "personal_ability"),
                    dressingUndressing: returnValueFromObject("dressingUndressing", "personal_ability"),
                    toileting: returnValueFromObject("toileting", "personal_ability"),
                    cleaningTeeth: returnValueFromObject("cleaningTeeth", "personal_ability"),
                    hairCareGrooming: returnValueFromObject("hairCareGrooming", "personal_ability"),
                    shaving: returnValueFromObject("shaving", "personal_ability"),
                    cuttingNails: returnValueFromObject("cuttingNails", "personal_ability"),
                    choosingClothes: returnValueFromObject("choosingClothes", "personal_ability"),
                    puttingOnFootwear: returnValueFromObject("puttingOnFootwear", "personal_ability"),
                    standing: returnValueFromObject("standing", "personal_ability"),
                    transferring: returnValueFromObject("transferring", "personal_ability"),
                },
                // Step 14 - Skin Integrity
                {
                    drySkin: returnValueFromObject("drySkin", "skin_integrity", "None"),
                    drySkinDescription: returnValueFromObject("drySkinDescription", "skin_integrity"),
                    redAreas: returnValueFromObject("redAreas", "skin_integrity", "None"),
                    redAreasDescription: returnValueFromObject("redAreasDescription", "skin_integrity"),
                    bruiseAreas: returnValueFromObject("bruiseAreas", "skin_integrity", "None"),
                    bruiseAreasDescription: returnValueFromObject("bruiseAreasDescription", "skin_integrity"),
                    scarAreas: returnValueFromObject("scarAreas", "skin_integrity", "None"),
                    scarAreasDescription: returnValueFromObject("scarAreasDescription", "skin_integrity"),
                    rashes: returnValueFromObject("rashes", "skin_integrity", "None"),
                    rashesDescription: returnValueFromObject("rashesDescription", "skin_integrity"),
                    skinTears: returnValueFromObject("skinTears", "skin_integrity", "None"),
                    skinTearsDescription: returnValueFromObject("skinTearsDescription", "skin_integrity"),
                    pressureRelatedUlcers: returnValueFromObject("pressureRelatedUlcers", "skin_integrity", "None"),
                    pressureRelatedUlcersDescription: returnValueFromObject("pressureRelatedUlcersDescription", "skin_integrity"),
                    circulatoryRelatedUlcers: returnValueFromObject("circulatoryRelatedUlcers", "skin_integrity", "None"),
                    circulatoryRelatedUlcersDescription: returnValueFromObject("circulatoryRelatedUlcersDescription", "skin_integrity"),
                    admissionWaterlowScore: returnValueFromObject("admissionWaterlowScore", "skin_integrity"),
                    otherCommentsSkinIntegrity: returnValueFromObject("otherCommentsSkinIntegrity", "skin_integrity"),
                },
                // Step 15 - Sexuality / Privacy
                {
                    howIsSexualityExpressed: returnValueFromObject("howIsSexualityExpressed", "sexuality_privacy"),
                    interestInSexualActivity: returnValueFromObject("interestInSexualActivity", "sexuality_privacy"),
                    responseToTouch: returnValueFromObject("responseToTouch", "sexuality_privacy"),
                    sexualOrgansMedicalConditions: returnValueFromObject("sexualOrgansMedicalConditions", "sexuality_privacy"),
                    previousExaminationBreast: returnValueFromObject("previousExaminationBreast", "sexuality_privacy", false),
                    previousExaminationBreastDate: returnValueFromObject("previousExaminationBreastDate", "sexuality_privacy"),
                    previousExaminationCervix: returnValueFromObject("previousExaminationCervix", "sexuality_privacy", false),
                    previousExaminationCervixDate: returnValueFromObject("previousExaminationCervixDate", "sexuality_privacy"),
                    previousExaminationProstate: returnValueFromObject("previousExaminationProstate", "sexuality_privacy", false),
                    previousExaminationProstateDate: returnValueFromObject("previousExaminationProstateDate", "sexuality_privacy"),
                    otherCommentsSexualityPrivacy: returnValueFromObject("otherCommentsSexualityPrivacy", "sexuality_privacy"),
                },
                // Step 16 - Values / Beliefs
                {
                    religiousChurchBeliefs: returnValueFromObject("religiousChurchBeliefs", "values_beliefs"),
                    wishesToContinueBeliefs: returnValueFromObject("wishesToContinueBeliefs", "values_beliefs"),
                    culturalInfluences: returnValueFromObject("culturalInfluences", "values_beliefs"),
                    burialCremationPreference: returnValueFromObject("burialCremationPreference", "values_beliefs"),
                    otherCommentsValuesBeliefs: returnValueFromObject("otherCommentsValuesBeliefs", "values_beliefs"),
                },
                // Step 17 - Orientation to Facility and Services
                {
                    understoodResidentCodeOfRights: returnValueFromObject("understoodResidentCodeOfRights", "orientation", false),
                    understoodPhilosophyOfTheFacility: returnValueFromObject("understoodPhilosophyOfTheFacility", "orientation", false),
                    understoodComplaintsComplimentsProcedure: returnValueFromObject("understoodComplaintsComplimentsProcedure", "orientation", false),
                    understoodStandardsOfService: returnValueFromObject("understoodStandardsOfService", "orientation", false),
                    shownCommunalLounge: returnValueFromObject("shownCommunalLounge", "orientation", false),
                    shownDiningRoom: returnValueFromObject("shownDiningRoom", "orientation", false),
                    shownBathroomFacilities: returnValueFromObject("shownBathroomFacilities", "orientation", false),
                    shownNoticeBoard: returnValueFromObject("shownNoticeBoard", "orientation", false),
                    shownRecreationRoom: returnValueFromObject("shownRecreationRoom", "orientation", false),
                    shownManagersOffice: returnValueFromObject("shownManagersOffice", "orientation", false),
                    otherCommentsOrientation: returnValueFromObject("otherCommentsOrientation", "orientation"),
                },
                // Step 18 - RN Signature
                {
                    // createdBy: "", // for displaying RN Name
                    registeredNurseSignature: returnValueFromObject("registeredNurseSignature", "registered_nurse_signature"),
                    dateSigned: returnValueFromObject("dateSigned", "registered_nurse_signature"),
                },
            ],
        };
    },
    validations() {
        const dateType = {
            required,
            isDateFormat,
            $lazy: true,
            $autoDirty: true,
        };

        const timeType = {
            required,
            isTimeFormat,
            $lazy: true,
            $autoDirty: true,
        };

        // TEXT TYPES: default: 191, mid:500, long:1000
        function text(minimum = 3, maximum = 191, isRequired = false) {
            var rule = {
                required,
                isTextWithSpace,
                minLength: minLength(minimum),
                maxLength: maxLength(maximum),
                $lazy: true,
                $autoDirty: true,
            };

            if (!isRequired) {
                rule = Object.assign({}, rule);
                delete rule["required"];
            }
            return rule;
        }

        // INT TYPES:
        function intType(minimum = 0, maximum = 10, isRequired = false) {
            var rule = {
                required,
                integer,
                minValue: minValue(minimum),
                maxValue: maxValue(maximum),
                $lazy: true,
                $autoDirty: true,
            };

            if (!isRequired) {
                rule = Object.assign({}, rule);
                delete rule["required"];
            }
            return rule;
        }

        // DECIMAL TYPES:
        function decimalType(minimum = 0, maximum = 10, isRequired = false) {
            var rule = {
                required,
                decimal,
                minValue: minValue(minimum),
                maxValue: maxValue(maximum),
                $lazy: true,
                $autoDirty: true,
            };

            if (!isRequired) {
                rule = Object.assign({}, rule);
                delete rule["required"];
            }
            return rule;
        }

        // ARRAY TYPES
        function inOptions(options, isRequired = false) {
            var rule = {
                required,
                inOptions: inArray(options),
                $lazy: true,
                $autoDirty: true,
            };

            if (!isRequired) {
                rule = Object.assign({}, rule);
                delete rule["required"];
            }
            return rule;
        }

        // Use for types with no 'required' parameter
        function notRequired(type) {
            var validations = Object.assign({}, type);
            delete validations["required"];

            return validations;
        }

        return {
            step: [
                // Step 1 - Activity Level
                {
                    optionalActivityTime: notRequired(timeType),
                    preferredActivities: {
                        // $each: helpers.forEach({ //TODO: figure out why we can't validate each entry
                        //     minLength: minLength(3),
                        //     required
                        // }),
                        $lazy: true,
                        $autoDirty: true,
                    },
                    interestsHobbies: {
                        // $each: helpers.forEach({ //TODO: figure out why we can't validate each entry
                        //     minLength: minLength(3),
                        //     required
                        // }),
                        $lazy: true,
                        $autoDirty: true,
                    },
                    orientedToTime: inOptions(this.trueFalseOptions, true),
                    orientedToPlace: inOptions(this.trueFalseOptions, true),
                    recognisesFamily: inOptions(this.trueFalseOptions, true),
                    recognisesStaff: inOptions(this.trueFalseOptions, true),
                    understandsConversation: inOptions(this.yesNoDontKnowOptions),
                    attentionSpan: inOptions(this.goodPoorFairOptions),
                    otherCommentsActivity: text(3, 500),
                },

                // Step 2 - Sleep Patterns
                {
                    preferredBedtime: notRequired(timeType),
                    preferredWakingTime: notRequired(timeType),
                    numberOfPillows: intType(0, 10, true),
                    wakesUpAtNightTime: notRequired(timeType),
                    wakesUpAtNightReason: text(),
                    dreamsNightmaresFears: inOptions(this.yesNoOptions, true),
                    wandersAtNight: inOptions(this.yesNoOptions, true),
                    sleepAidWarmDrink: inOptions(this.trueFalseOptions, true),
                    sleepAidMedication: inOptions(this.trueFalseOptions, true),
                    sleepAidRadioMusic: inOptions(this.trueFalseOptions, true),
                    daytimeRestsNaps: inOptions(this.yesNoOptions, true),
                    otherCommentsSleepPatterns: text(3, 500),
                },

                // Step 3 - Mobility
                {
                    fullyMobile: inOptions(this.yesNoOptions, true),
                    feetAndNailCondition: text(),
                    limitationsInMovementDescription: text(3, 500),
                    podiatryRequired: inOptions(this.yesNoOptions, true),
                    mobilityAidsUsed: text(3, 500),
                    wandering: inOptions(this.yesNoOptions, true),
                    typeOfWanderingDescription: text(3, 500),
                    typeOfPreferredFootwear: text(3, 191),
                    typeOfPreferredFootwearSafe: inOptions(this.trueFalseOptions),
                    riskOfFallingBalance: inOptions(this.trueFalseOptions, true),
                    riskOfFallingBalanceComment: text(),
                    riskOfFallingLimbStrength: inOptions(this.trueFalseOptions, true),
                    riskOfFallingLimbStrengthComment: text(),
                    riskOfFallingMobility: inOptions(this.trueFalseOptions, true),
                    riskOfFallingMobilityComment: text(),
                    riskOfFallingUseOfAids: inOptions(this.trueFalseOptions, true),
                    riskOfFallingUseOfAidsComment: text(),
                    riskOfFallingSight: inOptions(this.trueFalseOptions, true),
                    riskOfFallingSightComment: text(),
                    riskOfFallingMentalState: inOptions(this.trueFalseOptions, true),
                    riskOfFallingMentalStateComment: text(),
                    // levelOfRisk: intType(0,20,true),
                },

                // Step 4 - Nutrition
                {
                    admissionHeight: intType(0, 300, true),
                    admissionWeight: intType(0, 300, true),
                    weightHeightAppropriate: inOptions(this.yesNoOptions, true),
                    recentWeightGainLoss: inOptions(this.yesNoOptions, true),
                    recentWeightGainLossDescription: text(),
                    oralHealth: inOptions(this.goodPoorOptions, true),
                    naturalTeethUpper: inOptions(this.trueFalseOptions, true),
                    naturalTeethLower: inOptions(this.trueFalseOptions, true),
                    denturesUpper: inOptions(this.trueFalseOptions, true),
                    denturesLower: inOptions(this.trueFalseOptions, true),
                    remainingTeethCondition: text(3, 191, true),
                    lastDentalExamination: notRequired(dateType),
                    dentistIfKnown: text(),
                    swallowingDifficulties: inOptions(this.yesNoOptions, true),
                    swallowingDifficultiesDescription: text(),
                    otherCommentsNutrition: text(3, 500),
                },

                // Step 5 - Elimination
                {
                    admissionUrinalysis: inOptions(this.yesNoOptions, true),
                    urinaryContinent: inOptions(this.yesNoOptions, true),
                    urinaryContinentPattern: text(3, 191, false),
                    faecalContinent: inOptions(this.yesNoOptions, true),
                    faecalContinentPattern: text(3, 191, false),
                    previousContinence: inOptions(this.yesNoOptions, true),
                    previousContinenceDescription: text(3, 191, false),
                    historyOfUTI: inOptions(this.yesNoOptions, true),
                    historyOfUTILastInfection: notRequired(dateType),
                    voidingUrineDaytimePattern: text(3, 191, false),
                    voidingUrineNighttimePattern: text(3, 191, false),
                    bowelRoutineDaytimePattern: text(3, 191, false),
                    bowelRoutineNighttimePattern: text(3, 191, false),
                    bowelRelatedMedicalHistory: inOptions(this.yesNoOptions, true),
                    bowelRelatedMedicalHistoryDescription: text(3, 191),
                    furtherAssessmentRequired: inOptions(this.yesNoOptions, true),
                    otherCommentsElimination: text(3, 500),
                },

                // Step 6 - Perception
                {
                    primaryLanguage: text(3, 191, true),
                    secondaryLanguage: text(3, 191, true),
                    understandEnglish: inOptions(this.yesNoOptions, true),
                    speech: inOptions(["Relevant", "Irrelevant"], true),
                    rateOfSpeech: inOptions(["Talks a lot", "Minimal words"], true),
                    coherent: inOptions(this.yesNoOptions, true),
                    unusualWordsPattern: inOptions(this.yesNoOptions, true),
                    hallucinations: inOptions(this.yesNoOptions, true),
                    hallucinationsDescription: text(),
                    delusions: inOptions(this.yesNoOptions, true),
                    delusionsDescription: text(),
                    suddenChangeInTopic: inOptions(this.yesNoOptions, true),
                    otherCommentsPerception: text(3, 500),
                },
                // Step 7 - Hearing
                {
                    leftEar: inOptions(this.goodPoorFairOptions, true),
                    rightEar: inOptions(this.goodPoorFairOptions, true),
                    hearingAid: inOptions(this.noneLeftRightBothOptions, true),
                    waxBuildUp: inOptions(this.noneLeftRightBothOptions, true),
                    needsHearingAssessment: inOptions(this.yesNoOptions, true),
                    otherCommentsHearing: text(3, 500),
                },
                // Step 8 - Sight
                {
                    leftEye: inOptions(this.sightOptions, true),
                    rightEye: inOptions(this.sightOptions, true),
                    wearsSpectacles: inOptions(this.yesNoOptions, true),
                    wearsSpectaclesReason: text(3, 500),
                    eyeDrops: inOptions(this.yesNoOptions, true),
                    eyeDropsDetails: text(3, 191),
                    otherCommentsSight: text(3, 500),
                },
                // Step 9 - Mental Ability
                {
                    mentalCapacity: inOptions(this.yesNoOptions, true),
                    alertResponsiveBright: inOptions(this.alertOptions, true),
                    ableToAssistInPlanningCare: inOptions(this.yesNoOptions, true),
                    decreaseThoughtConcentration: inOptions(this.mildModerateSevereOptions, true),
                    confusion: inOptions(this.yesNoOptions, true),
                    shortTermMemory: inOptions(this.goodPoorFairOptions, true),
                    longTermMemory: inOptions(this.goodPoorFairOptions, true),
                    otherCommentsMentalAbility: text(3, 500),
                },
                // Step 10 - Behavioural Assessment
                {
                    insightToDiagnosis: inOptions(this.yesNoOptions, true),
                    episodesOfWandering: inOptions(this.yesNoOptions, true),
                    situationClientFindsChallenging: inOptions(this.yesNoOptions, true),
                    premorbidPersonality: inOptions(this.yesNoOptions, true),
                    premorbidPersonalityDescription: text(3, 191),
                    sourcesOfConflict: inOptions(this.yesNoOptions, true),
                    sourcesOfConflictDescription: text(3, 191),
                    suggestionsToDiffuseConflict: text(3, 191),
                    furtherBehaviourAssessmentRequired: inOptions(this.yesNoOptions, true),
                    otherCommentsBehaviouralAssessment: text(3, 500),
                },
                // Step 11 - Depression Assessment
                {
                    happyMostOfTheTime: inOptions(this.yesNoOptions, true),
                    expressAFeelingOfHelpfulness: inOptions(this.yesNoOptions, true),
                    cryOften: inOptions(this.yesNoOptions, true),
                    cryOftenDescription: text(3, 191),
                    expressAWishOfSelfHarm: inOptions(this.yesNoOptions, true),
                    expressAWishOfSelfHarmDescription: text(3, 191),
                    otherCommentsDepressionAssessment: text(3, 500),
                },
                // Step 12 - Pain Assessment
                {
                    clientHavePain: inOptions(this.noneAcuteChronicOptions, true),
                    clientHavePainWhen: notRequired(dateType),
                    clientHavePainWhere: text(),
                    possibleCauseOfPain: text(),
                    signsOfPain: text(),
                    typeOfPain: text(),
                    intensityOfPain: text(),
                    painMedication: inOptions(this.yesNoOptions),
                    painMedicationMedicines: text(),
                    painAssessmentRequired: inOptions(this.yesNoOptions),
                    otherCommentsPainAssessment: text(3, 500),
                },
                // Step 13 - Personal Ability
                {
                    showering: inOptions(this.abilityOptions, true),
                    washing: inOptions(this.abilityOptions, true),
                    dryingSelf: inOptions(this.abilityOptions, true),
                    dressingUndressing: inOptions(this.abilityOptions, true),
                    toileting: inOptions(this.abilityOptions, true),
                    cleaningTeeth: inOptions(this.abilityOptions, true),
                    hairCareGrooming: inOptions(this.abilityOptions, true),
                    shaving: inOptions(this.abilityOptions, true),
                    cuttingNails: inOptions(this.abilityOptions, true),
                    choosingClothes: inOptions(this.abilityOptions, true),
                    puttingOnFootwear: inOptions(this.abilityOptions, true),
                    standing: inOptions(this.abilityOptions, true),
                    transferring: inOptions(this.abilityOptions, true),
                },
                // Step 14 - Skin Integrity
                {
                    drySkin: inOptions(this.skinIntegrityOptions, true),
                    drySkinDescription: text(),
                    redAreas: inOptions(this.skinIntegrityOptions, true),
                    redAreasDescription: text(),
                    bruiseAreas: inOptions(this.skinIntegrityOptions, true),
                    bruiseAreasDescription: text(),
                    scarAreas: inOptions(this.skinIntegrityOptions, true),
                    scarAreasDescription: text(),
                    rashes: inOptions(this.skinIntegrityOptions, true),
                    rashesDescription: text(),
                    skinTears: inOptions(this.skinIntegrityOptions, true),
                    skinTearsDescription: text(),
                    pressureRelatedUlcers: inOptions(this.skinIntegrityOptions, true),
                    pressureRelatedUlcersDescription: text(),
                    circulatoryRelatedUlcers: inOptions(this.skinIntegrityOptions, true),
                    circulatoryRelatedUlcersDescription: text(),
                    admissionWaterlowScore: text(1, 500),
                    otherCommentsSkinIntegrity: text(3, 500),
                },
                // Step 15 - Sexuality / Privacy
                {
                    howIsSexualityExpressed: text(3, 191, true),
                    interestInSexualActivity: text(3, 191, true),
                    responseToTouch: text(3, 191, true),
                    sexualOrgansMedicalConditions: text(3, 191, true),
                    previousExaminationBreast: inOptions(this.trueFalseOptions, true),
                    previousExaminationBreastDate: notRequired(dateType),
                    previousExaminationCervix: inOptions(this.trueFalseOptions, true),
                    previousExaminationCervixDate: notRequired(dateType),
                    previousExaminationProstate: inOptions(this.trueFalseOptions, true),
                    previousExaminationProstateDate: notRequired(dateType),
                    otherCommentsSexualityPrivacy: text(3, 500),
                },
                // Step 16 - Values / Beliefs
                {
                    religiousChurchBeliefs: text(3, 191, true),
                    wishesToContinueBeliefs: inOptions(this.yesNoOptions, true),
                    culturalInfluences: text(3, 191, true),
                    burialCremationPreference: text(3, 191, true),
                    otherCommentsValuesBeliefs: text(3, 500),
                },
                // Step 17 - Orientation to Facility and Services
                {
                    understoodResidentCodeOfRights: inOptions(this.trueFalseOptions, true),
                    understoodPhilosophyOfTheFacility: inOptions(this.trueFalseOptions, true),
                    understoodComplaintsComplimentsProcedure: inOptions(this.trueFalseOptions, true),
                    understoodStandardsOfService: inOptions(this.trueFalseOptions, true),
                    shownCommunalLounge: inOptions(this.trueFalseOptions, true),
                    shownDiningRoom: inOptions(this.trueFalseOptions, true),
                    shownBathroomFacilities: inOptions(this.trueFalseOptions, true),
                    shownNoticeBoard: inOptions(this.trueFalseOptions, true),
                    shownRecreationRoom: inOptions(this.trueFalseOptions, true),
                    shownManagersOffice: inOptions(this.trueFalseOptions, true),
                    otherCommentsOrientation: text(3, 500),
                },
                // Step 18 - RN Signature
                {
                    registeredNurseSignature: text(3, 191, true),
                    dateSigned: dateType,
                },
            ],
        };
    },

    computed: {},

    methods: {
        onClickNext: function () {
            // Validate fields on current step
            this.v$.step[this.currentStep - 1].$touch();
            console.log("TT" + this.step[0].optionalActivityTime + "TT", "TT" + this.step[0].otherCommentsActivity + "TT");

            // if there are no errors in the current step
            if (!this.v$.step[this.currentStep - 1].$error) {
                // All steps
                if (this.currentStep < this.maxStep) {
                    // Proceed to next step
                    this.currentStep++;

                    // Final step
                } else if (this.currentStep == this.maxStep) {
                    // Initialize alerts and toast notifications for displaying message
                    const toastError = Swal.mixin({
                        toast: true,
                        position: "top-end",
                        showConfirmButton: true,
                    });
                    const alertSuccess = Swal.mixin({
                        position: "center",
                        showConfirmButton: true,
                        timer: 3000,
                        timerProgressBar: true,
                    });

                    // Set variables for server side validation
                    const endpoint = location.protocol + "//" + window.location.host + "/api/nursing-assessment/" + process.env.MIX_API_KEY;

                    // Show button spinner
                    document.getElementById("submit-btn").innerHTML = '<i class="fa fa-circle-notch fa-spin"></i>';
                    document.getElementById("submit-btn").disabled = true;
                    document.getElementById("last-back-btn").setAttribute("style", "display:none !important");
                    document.getElementById("last-save-btn").setAttribute("style", "display:none !important");

                    // Loop through all fields
                    const formData = {};

                    for (let i = 0; i < this.maxStep; i++) {
                        for (const [key, value] of Object.entries(this.step[i])) {
                            // formData.append(key, value);
                            formData[key] = value;
                        }
                    }

                    // Add residentID and userID
                    formData["residentID"] = this.residentId;
                    formData["userID"] = this.userId;
                    console.log(formData);

                    // Call the store endpoint
                    axios
                        .post(endpoint, formData)
                        .then((response) => {
                            if (response.data.error == 403) {
                                console.log("Bad Request");
                                console.log(response.data);

                                // Show bad request toast message. This error occurs when the input fields are not in the expected format
                                toastError.fire({
                                    type: "warning",
                                    title: "The input fields are malformed and cant be processed.",
                                });
                            } else {
                                if (response.data.id == null) {
                                    console.log("Validation Error");
                                    console.log(response.data);

                                    // Show validation toast message. This error only occurs when the javascript validation is bypassed and the API endpoint returned validation error instead of the resident object
                                    toastError.fire({
                                        type: "warning",
                                        title: "There are validation errors found on the form.",
                                    });
                                } else {
                                    console.log("Success");
                                    console.log(response.data);

                                    // Show success alert message and redirect to the index page. This occurs when the form submission is successful and the resident object was added to the DB. Thi
                                    this.formSuccess = true;
                                    window.residentId = this.residentId;

                                    alertSuccess
                                        .fire({
                                            title: "Success!",
                                            text: "The nursing assessment has been added.",
                                            icon: "success",
                                        })
                                        .then(function () {
                                            window.location = "/residents/" + window.residentId + "/nursing-assessments";
                                        });
                                }
                            }
                        })
                        .catch((err) => {
                            console.log("Endpoint Error!");
                            console.log(err.response);
                            toastError.fire({type: "warning", title: "Sorry, request failed to submit."}); // this error occurs when endpoint is wrong
                        });
                }
                this.percentageComplete = Math.round((this.currentStep / this.maxStep) * 100);
            }
        },
        onClickBack: function () {
            if (this.currentStep > 1) {
                this.currentStep--;
            }
            this.percentageComplete = Math.round((this.currentStep / this.maxStep) * 100);
        },
        addTagPreferredActivities(newTag) {
            this.preferredActivitiesOptions.push(newTag);
            this.step[0].preferredActivities.push(newTag);
        },
        addTagInterestsHobbies(newTag) {
            this.interestsHobbiesOptions.push(newTag);
            this.step[0].interestsHobbies.push(newTag);
        },
    },
};
</script>

<!-- <style src="vue-multiselect/dist/vue-multiselect.min.css"></style> -->
